import { generateBetItemId } from '../utils/ticketHelpers';
import { buildCombiBetItems } from '../utils/topCombi';
import { buildMatchKombiBetItem } from '../utils/matchKombi';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { BetItem } from './BettingSlipDrawer';
import { getCatalogHome, getLiveEvents, getPreEvents, getTournaments, getSports, getTopEvents, placeBet, getMe, getWallet, getBetHistory, getEvent, BASE_URL } from '../api/api';
import { EventSnapshot, Market, Outcome, SportItem, UserOut, WalletOut, TicketOut, PlaceBetIn, TournamentItem, TopEventItem, TopMatchKombiItem, TopCombiItem } from '../api/types';
import { OddsButton } from './OddsButton';
import MatchDetailPage from './MatchDetailPage';
import DesktopMyBetsPage from './DesktopMyBetsPage';
import DesktopAccountPage from './DesktopAccountPage';
import { useFavorites } from '../contexts/FavoritesContext';
import { TOP_WETTBEWERBE, TOURNAMENT_ID_MAP } from '../constants/tipico_menu';
import { useTheme } from '../contexts/ThemeContext';
import { DesktopLeftSidebar, SPORT_EMOJI, TOP_COMPETITIONS } from './widgets/DesktopLeftSidebar';
import { DesktopRightSidebar } from './widgets/DesktopRightSidebar';
import { DesktopCenterContent } from './widgets/DesktopCenterContent';

import { useBetSlipQuote } from '../hooks/useBetSlipQuote';
import { SearchModal } from './widgets/SearchModal';
import { Footer } from './widgets/Footer';
import { useTranslation, Locale, LOCALE_FLAGS, LOCALE_LABELS } from '../i18n';
import { translateMarketFilterLabel } from '../utils/marketTranslations';
import {
  getMarketAttr,
  getLayoutGroupForMarketId,
  getMarketOptionsForSport,
  find1x2Market,
  findRestzeitGeneric,
  findOUGeneric,
  findNextGoal,
  find1stHalfNextGoal,
  findHandicap,
  findSatzHandicap,
  findBTTSGeneric,
  findDCGeneric,
  findH2H,
  find1stHalfMarket,
  find1stHalfRestzeitGeneric,
  findSatzSieger,
  findOddEven,
  findTeamTotal,
  findQuarterTotal,
  findOvertimeYesNo,
  find2ndHalfHandicap,
  find2ndHalfTotal,
  findQuarter1x2,
  findPointsTotal,
  findSatzTotal,
  isFilterAvailable
} from '../utils/marketParsers';
import { getActiveLivestreams } from '../api/stream';
import { useActiveStreams } from '../hooks/useActiveStreams';
import { choose, getCombinations, calculateSystemPossibleWin } from '../utils/combinations';
import { buildEventGroups } from '../utils/ticketHelpers';
import { formatNotification } from '../utils/notificationFormatter';
import { mergeSnapshotWithPatch, applyStreamUpdates } from '../utils/streamMerge';
import { liveStreamManager, preStreamManager } from '../services/CatalogStreamManager';

const SPORT_NAME_TO_ID: Record<string, string> = {
  'Soccer': 'sr:sport:1',
  'Basketball': 'sr:sport:2',
  'Tennis': 'sr:sport:5',
  'Ice Hockey': 'sr:sport:4',
  'Handball': 'sr:sport:6',
  'Table Tennis': 'sr:sport:20',
  'Volleyball': 'sr:sport:23',
  'Baseball': 'sr:sport:3'
};

// ── Helpers ────────────────────────────────────────────────────────────────

const LockIcon = () => (<svg className="w-4 h-4 text-gray-400 block" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>);

function formatOdds(val: string | number): string {
  const n = parseFloat(String(val));
  if (isNaN(n) || n <= 1) return '-';
  return n.toFixed(2).replace('.', ',');
}

function formatClock(ev: EventSnapshot, t?: (k: string) => string): string {
  const sport = ev.event?.sport_name;

  const sportId = ev.event?.sport_id;

  if (sport === 'Baseball' || sportId === 'sr:sport:3') {
    const text = ev.status?.match_status_text || '';
    const numMatch = text.match(/\d+/);
    if (numMatch) {
      return `${numMatch[0]}/9`;
    }
    if (text.toLowerCase().includes('break')) {
      const ps = ev.status?.period_scores;
      if (ps && ps.length > 0) {
        return `${ps.length}/9`;
      }
      return 'Break';
    }
    if (text) return text;
  }

  const useStatusText = sport === 'Basketball' || sport === 'Tennis' || sport === 'Table Tennis' || sport === 'Volleyball' || sportId === 'sr:sport:2' || sportId === 'sr:sport:5' || sportId === 'sr:sport:20' || sportId === 'sr:sport:23';

  if (useStatusText && ev.status?.match_status_text) {
    return ev.status.match_status_text.replace(/\b(?:1st|first)\b/gi, '1.').replace(/\b(?:2nd|second)\b/gi, '2.').replace(/\b(?:3rd|third)\b/gi, '3.').replace(/\b(?:4th|fourth)\b/gi, '4.').replace(/\b(\d+)(st|nd|rd|th)\b/gi, '$1.');
  }

  const stLower = (ev.status?.match_status_text || '').toLowerCase();
  if (stLower === 'halftime' || stLower === 'halbzeit' || stLower === 'devre arası' || stLower === 'ht') {
    return t ? t('live.ht') : 'HT';
  }
  if (stLower === 'ended' || stLower === 'closed' || stLower === 'finished' || stLower === 'ft' || stLower === 'beendet' || stLower === 'bitti' || stLower === 'ms') {
    return t ? t('live.ft') : 'FT';
  }

  if (ev.status?.clock) {
    const minStr = ev.status.clock.split(':')[0];
    if (minStr.includes('+')) {
      return minStr + "'";
    }
    const minNum = parseInt(minStr, 10);
    if (!isNaN(minNum)) {
      const displayMin = minNum + 1;

      if (sport === 'Soccer' || sportId === 'sr:sport:1') {
        const matchStatusCode = (ev.status as any)?.match_status;
        const isFirstHalf = stLower.includes('1.') || stLower.includes('1st') || stLower.includes('first') || stLower.includes('ilk') || stLower === '1' || matchStatusCode === '6' || matchStatusCode === 6;
        const isExtraTime = stLower.includes('et') || stLower.includes('extra') || stLower.includes('uzatma') || stLower.includes('verläng') || stLower.includes('verlaeng') || stLower.includes('overtime') || matchStatusCode === '41' || matchStatusCode === '42' || matchStatusCode === 41 || matchStatusCode === 42;

        const is90 = minStr === '90';
        const is45 = minStr === '45';

        if ((is90 && !isExtraTime && !isFirstHalf) || (is45 && isFirstHalf)) {
          const baseMin = is90 ? 90 : 45;
          // @ts-ignore: stoppage_time exists in SSE payload
          const stoppage = ev.status?.stoppage_time;
          if (stoppage && stoppage !== '0:00' && typeof stoppage === 'string') {
            const parts = stoppage.split(':');
            if (parts.length === 2) {
              const sMin = parseInt(parts[0], 10);
              const sSec = parseInt(parts[1], 10);
              if (!isNaN(sMin) && !isNaN(sSec)) {
                let added = sMin;
                if (sSec > 0 || sMin === 0) added += 1;
                return `${baseMin}+${added}'`;
              }
            }
          }
          return `${baseMin}'`;
        }

        if (isFirstHalf && displayMin > 45 && !isExtraTime) {
          return `45+${displayMin - 45}'`;
        }
        if (displayMin > 90 && !isExtraTime && !isFirstHalf) {
          return `90+${displayMin - 90}'`;
        }
      }

      return displayMin + "'";
    }
    return minStr + "'";
  }
  if (ev.status?.match_status_text) {
    return ev.status.match_status_text.replace(/\b(?:1st|first)\b/gi, '1.').replace(/\b(?:2nd|second)\b/gi, '2.').replace(/\b(?:3rd|third)\b/gi, '3.').replace(/\b(?:4th|fourth)\b/gi, '4.').replace(/\b(\d+)(st|nd|rd|th)\b/gi, '$1.');
  }
  return 'Live';
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function isWithinHours(ev: EventSnapshot, hours: number): boolean {
  const starts = ev.event?.scheduled_at ?? ev.event?.start_time;
  if (!starts) return false;
  const d = new Date(starts).getTime();
  const now = Date.now();
  return d >= now && d <= now + hours * 3600_000;
}

// ── Props ──────────────────────────────────────────────────────────────────

interface DesktopHomePageProps {
  bets: BetItem[];
  onAddBet: (bet: BetItem) => void;
  onRemoveBet: (id: string) => void;
  onUpdateBet: (id: string, patch: Partial<BetItem>) => void;
  onClearAll: () => void;
  onLoginClick: () => void;
  onRegisterClick: () => void;
  activePage: string;
  onNavigate: (id: string) => void;
}

// ── Component ──────────────────────────────────────────────────────────────

const DesktopHomePage: React.FC<DesktopHomePageProps> = ({
  bets, onAddBet, onRemoveBet, onUpdateBet, onClearAll,
  onLoginClick, onRegisterClick, activePage, onNavigate,
}) => {
  const { isFavorite } = useFavorites();
  const { theme, toggleTheme } = useTheme();
  const { t, locale, setLocale } = useTranslation();
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [liveEvents, setLiveEvents] = useState<EventSnapshot[]>([]);
  const [topLiveEvents, setTopLiveEvents] = useState<EventSnapshot[]>([]);
  const [preEvents, setPreEvents] = useState<EventSnapshot[]>([]);
  const [topPreEvents, setTopPreEvents] = useState<EventSnapshot[]>([]);
  const [topEventsApiData, setTopEventsApiData] = useState<TopEventItem[]>([]);
  const [topEventsSportTabs, setTopEventsSportTabs] = useState<{id: string, name: string}[]>([]);
  const [topEventsPageData, setTopEventsPageData] = useState<TopEventItem[]>([]);
  const initialSortRef = useRef<Record<string, number>>({});


  const [liveUpcomingEvents, setLiveUpcomingEvents] = useState<EventSnapshot[]>([]);
  const [liveStartsAfter, setLiveStartsAfter] = useState(0);
  const [liveStartsBefore, setLiveStartsBefore] = useState(12);
  const [loadingMoreLive, setLoadingMoreLive] = useState(false);
  const activeStreams = useActiveStreams();

  const [fetchedTournaments, setFetchedTournaments] = useState<TournamentItem[]>([]);
  const [sports, setSports] = useState<SportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activePreSport, setActivePreSport] = useState('sr:sport:1');
  const [activeSidebarSport, setActiveSidebarSport] = useState('sr:sport:1');
  const [activeLiveSport, setActiveLiveSport] = useState('sr:sport:1');
  const [sortBy, setSortBy] = useState<'zeit' | 'wettbewerb' | 'oddsAsc' | 'oddsDesc'>('zeit');
  const [sortModalOpen, setSortModalOpen] = useState(false);
  const [timeFilter, setTimeFilter] = useState<'all' | 'heute' | '3std' | '48std'>('all');
  const [stake, setStake] = useState('5');
  const [placing, setPlacing] = useState(false);
  const [betStatus, setBetStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    if (betStatus === 'success') {
      const timer = setTimeout(() => {
        setBetStatus('idle');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [betStatus]);

  useEffect(() => {
    if (activePage === 'live' && (activeLiveSport === 'sr:sport:1' || activeLiveSport === 'Soccer')) {
      setSortBy('wettbewerb');
    }
  }, [activePage, activeLiveSport]);

  const [betError, setBetError] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<EventSnapshot | null>(null);
  const [detailInitialTab, setDetailInitialTab] = useState<'all' | 'builder'>('all');

  // Betslip modes states
  const [betslipMode, setBetslipMode] = useState<'single' | 'combi' | 'system'>('combi');
  const [individualStakes, setIndividualStakes] = useState<Record<string, string>>({});
  const [systemSize, setSystemSize] = useState<number>(2);

  // Automatically adjust modes and sizes when selections count changes
  useEffect(() => {
    if (bets.length === 0) return;
    if (betslipMode === 'system' && bets.length < 3) {
      setBetslipMode('combi');
    }
    const maxK = bets.length - 1;
    if (systemSize < 2) {
      setSystemSize(2);
    } else if (systemSize > maxK) {
      setSystemSize(Math.max(2, maxK));
    }
  }, [bets.length, betslipMode, systemSize]);

  // Layout states for drop-downs
  const [topMarket1, setTopMarket1] = useState<string | number>('Tipp');
  const [topMarket2, setTopMarket2] = useState<string | number>('1. Halbzeit');
  const [topMarket3, setTopMarket3] = useState<string | number>('Doppelte Chance');
  const [topMarket4, setTopMarket4] = useState<string | number>('Über/Unter');
  const [topMarket5, setTopMarket5] = useState<string | number>('Beide Teams treffen');
  const [lastMinMarket1, setLastMinMarket1] = useState<string | number>('Tipp');
  const [lastMinMarket2, setLastMinMarket2] = useState<string | number>('1. Halbzeit');
  const [lastMinMarket3, setLastMinMarket3] = useState<string | number>('Doppelte Chance');
  const [lastMinMarket4, setLastMinMarket4] = useState<string | number>('Über/Unter');
  const [lastMinMarket5, setLastMinMarket5] = useState<string | number>('Beide Teams treffen');
  const [liveMarket1, setLiveMarket1] = useState<string | number>(1);
  const [liveMarket2, setLiveMarket2] = useState<string | number>(7);
  const [liveMarket3, setLiveMarket3] = useState<string | number>(18);
  const [liveMarket4, setLiveMarket4] = useState<string | number>(10);
  const [liveMarket5, setLiveMarket5] = useState<string | number>(29);
  const [upcomingMarket1, setUpcomingMarket1] = useState<string | number>('Tipp');
  const [upcomingMarket2, setUpcomingMarket2] = useState<string | number>('Über/Unter');
  const [upcomingMarket3, setUpcomingMarket3] = useState<string | number>('Handicap');
  const [upcomingMarket4, setUpcomingMarket4] = useState<string | number>('Doppelte Chance');
  const [upcomingMarket5, setUpcomingMarket5] = useState<string | number>('Beide Teams treffen');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [rightSidebarTab, setRightSidebarTab] = useState<'wettschein' | 'offene'>('wettschein');
  const [quickFilter, setQuickFilter] = useState<string | null>(null);
  const [fetchedCompetitionEvents, setFetchedCompetitionEvents] = useState<EventSnapshot[] | null>(null);
  const [fetchingCompetition, setFetchingCompetition] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const p = location.pathname;
    if (p.startsWith('/top-events')) {
      setQuickFilter('top-events');
    } else if (p.startsWith('/todays-matches')) {
      setQuickFilter('todays-matches');
    } else if (p.startsWith('/category/')) {
      const catName = decodeURIComponent(p.substring('/category/'.length));
      setQuickFilter('category:' + catName);
      setActivePreSport('sr:sport:1'); setActiveLiveSport('sr:sport:1');
    } else if (p.startsWith('/competition/')) {
      const compName = decodeURIComponent(p.substring('/competition/'.length));
      setQuickFilter('tournament:' + compName);
      setActivePreSport('sr:sport:1'); setActiveLiveSport('sr:sport:1');
    } else if (p === '/' || p === '/home') {
      setQuickFilter(null);
    }
  }, [location.pathname]);

  // Reset pre-match market choices when the sport changes
  useEffect(() => {
    let opts = getMarketOptionsForSport(activePreSport, false);
    if (activePreSport === 'sr:sport:1' || activePreSport === 'Soccer') {
      opts = [1, 60, 10, 18, 29];
    }
    setLastMinMarket1(opts[0] || 'Sieger');
    setLastMinMarket2(opts[1] || 'Über/Unter');
    setLastMinMarket3(opts[2] || 'Handicap');
    setLastMinMarket4(opts[3] || '');
    setLastMinMarket5(opts[4] || '');

    setTopMarket1(opts[0] || 'Sieger');
    setTopMarket2(opts[1] || 'Über/Unter');
    setTopMarket3(opts[2] || 'Handicap');
    setTopMarket4(opts[3] || '');
    setTopMarket5(opts[4] || '');
  }, [activePreSport]);

  // Reset live-match market choices when the sport changes
  useEffect(() => {
    const opts = getMarketOptionsForSport(activeLiveSport, true);
    setLiveMarket1(opts[0] || 'Sieger');
    setLiveMarket2(opts[1] || 'Über/Unter');
    setLiveMarket3(opts[2] || 'Handicap');
    setLiveMarket4(opts[3] || '');
    setLiveMarket5(opts[4] || '');
  }, [activeLiveSport]);

  const upcomingSport = activePage === 'live' ? activeLiveSport : activePreSport;

  // Reset upcoming market choices when the upcoming sport changes
  useEffect(() => {
    let opts = getMarketOptionsForSport(upcomingSport, false);
    if (activePage === 'live' && (upcomingSport === 'sr:sport:1' || upcomingSport === 'Soccer')) {
      opts = [1, 60, 10, 29, 18];
    }
    setUpcomingMarket1(opts[0] || 'Sieger');
    setUpcomingMarket2(opts[1] || 'Über/Unter');
    setUpcomingMarket3(opts[2] || 'Handicap');
    setUpcomingMarket4(opts[3] || '');
    setUpcomingMarket5(opts[4] || '');
  }, [upcomingSport, activePage]);

  // Nested sidebar states
  const [expandedTopSport, setExpandedTopSport] = useState<string | null>(null);
  const [expandedAlleSport, setExpandedAlleSport] = useState<string | null>(null);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [selectedTournaments, setSelectedTournaments] = useState<Set<string>>(new Set());
  const [topSportenTimeFilter, setTopSportenTimeFilter] = useState<'all' | '3hr' | '48hr'>('48hr');

  // Fetch tournament/category specific pre-events when selected
  useEffect(() => {
    const isCategory = quickFilter?.startsWith('category:');
    const isTournament = quickFilter?.startsWith('tournament:');
    const hasSelected = selectedTournaments.size > 0;

    if (!isCategory && !isTournament && !hasSelected) {
      setFetchedCompetitionEvents(null);
      return;
    }

    const targetCategories = new Set<string>();
    const targetTournaments = new Set<string>();

    if (isCategory) targetCategories.add(quickFilter!.split(':')[1]);
    if (isTournament) targetTournaments.add(quickFilter!.split(':')[1]);
    if (hasSelected) {
      selectedTournaments.forEach(t => targetTournaments.add(t));
    }

    // Static mapping from tournament name/key to Sportradar ID
    const TOURNAMENT_ID_MAP: Record<string, string> = {
      'DFB Pokal': 'sr:tournament:47',
      'DFB-Pokal': 'sr:tournament:47',
      '2. Bundesliga': 'sr:tournament:44',
      'Coupe de France': 'sr:tournament:331',
      'Serie A': 'sr:tournament:23',
      'LaLiga': 'sr:tournament:8',
      'La Liga': 'sr:tournament:8',
      'Bundesliga': 'sr:tournament:35',
      'Premier League': 'sr:tournament:17',
      'Ligue 1': 'sr:tournament:16',
      'Champions League': 'sr:tournament:7',
      'Europa League': 'sr:tournament:6',
    };

    const tournamentIds: string[] = [];
    const namesToLookup: string[] = [];

    targetTournaments.forEach(t => {
      if (t.startsWith('sr:tournament:')) {
        tournamentIds.push(t);
      } else if (TOURNAMENT_ID_MAP[t]) {
        tournamentIds.push(TOURNAMENT_ID_MAP[t]);
      } else {
        namesToLookup.push(t);
      }
    });

    // Lookup tournament IDs from our known events pool for categories or unmapped names
    if (namesToLookup.length > 0 || targetCategories.size > 0) {
      const knownEvents = [...preEvents];
      knownEvents.forEach(e => {
        const tId = e.event.tournament_id;
        const tName = e.event.tournament_name;
        const cName = e.event.category_name;
        if (tId) {
          if (targetCategories.size > 0 && cName && targetCategories.has(cName)) {
            tournamentIds.push(tId);
          }
          if (namesToLookup.length > 0 && tName && (namesToLookup.includes(tName) || namesToLookup.some(n => tName.includes(n)))) {
            tournamentIds.push(tId);
          }
        }
      });
    }

    const uniqueTournamentIds = Array.from(new Set(tournamentIds));

    if (uniqueTournamentIds.length === 0) {
      setFetchedCompetitionEvents(null);
      return;
    }

    let active = true;

    const fetchEvents = async () => {
      setFetchingCompetition(true);
      try {
        const resList = await Promise.all(
          uniqueTournamentIds.map(id => getPreEvents({ tournament_id: id }))
        );
        if (!active) return;

        // Remove duplicates if any
        const allFetched = resList.flatMap(r => r.items);
        const unique = Array.from(new Map(allFetched.map(e => [e.event_id, e])).values());
        setFetchedCompetitionEvents(unique);
      } catch (err) {
        console.error("Failed to fetch competition events:", err);
      } finally {
        if (active) setFetchingCompetition(false);
      }
    };

    fetchEvents();

    return () => {
      active = false;
    };
  }, [quickFilter, selectedTournaments]);

  const allEventsForSearch = useMemo(() => [...liveEvents, ...preEvents], [liveEvents, preEvents]);

  const handleEventClick = useCallback((ev: EventSnapshot) => {
    setDetailInitialTab('all');
    setSelectedEvent(ev);
  }, []);

  const toggleTournament = (t: string) => {
    setActivePreSport(activeSidebarSport);
    setSelectedTournaments(prev => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t); else next.add(t);
      return next;
    });
  };

  const toggleCountry = (tournaments: Record<string, any>) => {
    setActivePreSport(activeSidebarSport);
    const allT = Object.entries(tournaments).map(([name, val]) => {
      return (val && typeof val === 'object' && 'id' in val) ? val.id : name;
    });
    const allSelected = allT.every(t => selectedTournaments.has(t));
    setSelectedTournaments(prev => {
      const next = new Set(prev);
      if (allSelected) { allT.forEach(t => next.delete(t)); }
      else { allT.forEach(t => next.add(t)); }
      return next;
    });
  };

  const [user, setUser] = useState<UserOut | null>(null);
  const [wallet, setWallet] = useState<WalletOut | null>(null);
  const [tickets, setTickets] = useState<TicketOut[]>([]);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isLogged, setIsLogged] = useState(!!localStorage.getItem('access_token'));

  useEffect(() => {
    const handleAuthChanged = () => {
      const token = !!localStorage.getItem('access_token');
      setIsLogged(token);
      if (!token) {
        setUser(null);
        setWallet(null);
      }
    };
    window.addEventListener('auth-changed', handleAuthChanged);
    window.addEventListener('storage', handleAuthChanged);
    // Initial fetch if logged in on mount
    handleAuthChanged();
    return () => {
      window.removeEventListener('auth-changed', handleAuthChanged);
      window.removeEventListener('storage', handleAuthChanged);
    }
  }, []);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (isLogged) {
      getMe().then(setUser).catch(console.error);

      const fetchWalletAndTickets = () => {
        getWallet().then(setWallet).catch(console.error);

        // Sağ panelde "Açık Kuponlar" sekmesi açıksa biletleri çek
        if (rightSidebarTab === 'offene') {
          getBetHistory(200).then(setTickets).catch(console.error);
        }
      };

      fetchWalletAndTickets();

      // Sadece bakiye için arkaplan kontrolü (30sn), biletleri sadece aktifse çeker
      intervalId = setInterval(fetchWalletAndTickets, 30000);
    } else {
      setTickets([]);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isLogged, activePage, rightSidebarTab]);

  useEffect(() => {
    const handleBalanceChange = () => {
      if (isLogged) {
        getWallet().then(setWallet).catch(console.error);
        getBetHistory(200).then(setTickets).catch(console.error);
      }
    };
    window.addEventListener('balance-changed', handleBalanceChange);
    return () => window.removeEventListener('balance-changed', handleBalanceChange);
  }, [isLogged]);


  // ── Notification dropdown state removed ────────────────────────────────────────────

  const filterMaxPerSport = (events: EventSnapshot[] | undefined, max: number = 8) => {
    if (!events || !Array.isArray(events)) return [];
    const counts: Record<string, number> = {};
    return events.filter(ev => {
      const sport = ev.event?.sport_name;
      if (!sport) return true;
      counts[sport] = (counts[sport] || 0) + 1;
      return counts[sport] <= max;
    });
  };

  const loadData = useCallback(async (signal?: AbortSignal) => {
    try {
      if (activePage === 'live') {
        const [live, sp, home, top, topPage] = await Promise.all([
          getLiveEvents({ signal }),
          getSports(undefined, { signal }),
          getCatalogHome({ pre_within_hours: 24, pre_limit: 500 }, { signal }),
          getTopEvents({ limit: 50 }, { signal }).catch(() => ({ items: [] })),
          getTopEvents({ view: 'page', limit: 30 }, { signal }).catch(() => ({ items: [] }))
        ]);
        const filteredLive = filterMaxPerSport(live.items);
        const filteredPre = filterMaxPerSport(home.pre);
        setLiveEvents(filteredLive);
        setTopLiveEvents(filteredLive);
        setPreEvents(filteredPre);
        setTopPreEvents(filteredPre);
        setSports(sp.items);
        setTopEventsApiData(top.items || []);
        if ('sport_tabs' in top && Array.isArray(top.sport_tabs)) {
          setTopEventsSportTabs(top.sport_tabs.map((t: any) => ({ id: t.sport_id, name: t.name })));
        }
        setTopEventsPageData(topPage.items || []);
      } else {
        const [home, sp, top, topPage] = await Promise.all([
          getCatalogHome({ pre_within_hours: 24, pre_limit: 500 }, { signal }),
          getSports(undefined, { signal }),
          getTopEvents({ limit: 50 }, { signal }).catch(() => ({ items: [] })),
          getTopEvents({ view: 'page', limit: 30 }, { signal }).catch(() => ({ items: [] }))
        ]);
        const filteredLive = filterMaxPerSport(home.live);
        const filteredPre = filterMaxPerSport(home.pre);
        setLiveEvents(filteredLive);
        setTopLiveEvents(filteredLive);
        setPreEvents(filteredPre);
        setTopPreEvents(filteredPre);
        setSports(sp.items);
        setTopEventsApiData(top.items || []);
        if ('sport_tabs' in top && Array.isArray(top.sport_tabs)) {
          setTopEventsSportTabs(top.sport_tabs.map((t: any) => ({ id: t.sport_id, name: t.name })));
        }
        setTopEventsPageData(topPage.items || []);
      }
    } catch (err) {
      if (signal?.aborted) return;
      console.error('Failed to load initial data', err);
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [activePage]);

  useEffect(() => {
    setLoading(true);
    const abortController = new AbortController();
    loadData(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [loadData]);

  useEffect(() => {
    if (activePage === 'live' || activePage === 'bets') return;

    let startsBefore = 2160;
    if (timeFilter === 'heute') {
      const now = new Date();
      const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      startsBefore = Math.max(1, Math.ceil((endOfDay.getTime() - now.getTime()) / 3600000));
    } else if (timeFilter === '3std') {
      startsBefore = 3;
    } else if (timeFilter === '48std') {
      startsBefore = 48;
    }

    getTournaments({ starts_after_hours: 0, starts_before_hours: startsBefore, limit: 5000 })
      .then(res => setFetchedTournaments(res.items || []))
      .catch(console.error);
  }, [timeFilter, activePage]);

  useEffect(() => {
    if (!quickFilter?.startsWith('tournament:')) return;

    const trnName = quickFilter.substring('tournament:'.length);
    let trnId = TOURNAMENT_ID_MAP[trnName] || trnName;

    if (!trnId.startsWith('sr:tournament:')) {
      const found = fetchedTournaments.find(t => t.tournament_name === trnName);
      if (found && found.tournament_id) {
        trnId = found.tournament_id;
      }
    }

    if (trnId.startsWith('sr:tournament:')) {
      const abortController = new AbortController();
      setLoading(true);

      getPreEvents({ tournament_id: trnId, starts_before_hours: 168, sport_id: 'sr:sport:1' }, { signal: abortController.signal })
        .then(res => {
          if (res.items && res.items.length > 0) {
            setPreEvents(prev => {
              const existingIds = new Set(prev.map(e => e.event_id));
              const newEvents = res.items!.filter(e => !existingIds.has(e.event_id));
              return [...prev, ...newEvents];
            });
          }
        })
        .catch(e => {
          if (e.name !== 'AbortError') console.error('Failed to fetch tournament events', e);
        })
        .finally(() => {
          if (!abortController.signal.aborted) setLoading(false);
        });

      return () => abortController.abort();
    }
  }, [quickFilter, fetchedTournaments]);

  const loadLiveUpcomingData = useCallback(async (
    sa: number,
    sb: number,
    sport: string,
    append: boolean,
    options?: RequestInit
  ) => {
    try {
      if (append) setLoadingMoreLive(true);
      const isFav = sport === 'Favorites';
      const sportId = isFav ? undefined : (sport.startsWith('sr:sport:') ? sport : SPORT_NAME_TO_ID[sport]);

      const res = await getPreEvents({ starts_after_hours: sa, starts_before_hours: sb, sport_id: sportId }, options);

      if (!append) {
        setLiveUpcomingEvents(res.items || []);
      } else {
        setLiveUpcomingEvents(prev => {
          const newEvents = (res.items || []).filter(newItem => !prev.some(p => p.event_id === newItem.event_id));
          return [...prev, ...newEvents];
        });
      }
    } catch (e) {
      if ((e as any)?.name !== 'AbortError') {
        console.error('Failed to load live upcoming events', e);
      }
    } finally {
      if (!options?.signal?.aborted) {
        setLoadingMoreLive(false);
      }
    }
  }, []);

  useEffect(() => {
    if (activePage !== 'live') return;
    const abortController = new AbortController();
    setLiveStartsAfter(0);
    setLiveStartsBefore(12);
    loadLiveUpcomingData(0, 12, activeLiveSport, false, { signal: abortController.signal });
    return () => abortController.abort();
  }, [activeLiveSport, activePage, loadLiveUpcomingData]);

  const loadMoreLiveUpcoming = useCallback(() => {
    if (loadingMoreLive || activePage !== 'live') return;
    const nextAfter = liveStartsBefore;
    const nextBefore = liveStartsBefore + 12;
    setLiveStartsAfter(nextAfter);
    setLiveStartsBefore(nextBefore);
    loadLiveUpcomingData(nextAfter, nextBefore, activeLiveSport, true);
  }, [liveStartsBefore, activeLiveSport, loadLiveUpcomingData, loadingMoreLive, activePage]);

  useEffect(() => {
    const handleScroll = () => {
      if (activePage !== 'live') return;
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 200) {
        if (!loading && !loadingMoreLive) {
          loadMoreLiveUpcoming();
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loading, loadingMoreLive, activePage, loadMoreLiveUpcoming]);

  useEffect(() => {
    if (loading) return;

    const unsubLive = liveStreamManager.subscribe((upserts, deletes) => {
      if (upserts.length === 0 && deletes.length === 0) return;
      const applyUpdates = applyStreamUpdates(upserts, deletes, false);
      const applyUpdatesOnlyExisting = applyStreamUpdates(upserts, deletes, true);

      setLiveEvents(prev => applyUpdates(prev) || []);
      setTopLiveEvents(prev => applyUpdatesOnlyExisting(prev) || []);

      setSelectedEvent(prev => {
        if (!prev) return prev;
        const msg = upserts.find(u => u.event_id === prev.event_id);
        if (msg) return msg;
        if (deletes.includes(prev.event_id)) return null;
        return prev;
      });
    });

    const unsubPre = preStreamManager.subscribe((upserts, deletes) => {
      if (upserts.length === 0 && deletes.length === 0) return;
      const applyUpdates = applyStreamUpdates(upserts, deletes, false);
      const applyUpdatesOnlyExisting = applyStreamUpdates(upserts, deletes, true);

      setPreEvents(prev => applyUpdates(prev) || []);
      setFetchedCompetitionEvents(prev => prev ? (applyUpdatesOnlyExisting(prev) || prev) : null);
      setTopPreEvents(prev => applyUpdatesOnlyExisting(prev) || []);
      setLiveUpcomingEvents(prev => applyUpdatesOnlyExisting(prev) || []);

      setSelectedEvent(prev => {
        if (!prev) return prev;
        const msg = upserts.find(u => u.event_id === prev.event_id);
        if (msg) return msg;
        if (deletes.includes(prev.event_id)) return null;
        return prev;
      });
    });

    return () => {
      unsubLive();
      unsubPre();
    };
  }, [loading]);

  // Sport tabs
  const sportCounts: Record<string, number> = {};
  const sportNames: Record<string, string> = {};
  for (const ev of liveEvents) {
    const sId = ev.event.sport_id;
    const sName = ev.event.sport_name;
    if (sId && sName) {
      sportCounts[sId] = (sportCounts[sId] || 0) + 1;
      sportNames[sId] = sName;
    }
  }
  const SPORT_ORDER_BY_ID: Record<string, number> = {
    'sr:sport:1': 1, // Soccer
    'sr:sport:2': 2, // Basketball
    'sr:sport:5': 3, // Tennis
    'sr:sport:4': 4, // Ice Hockey
    'sr:sport:23': 5, // Volleyball
    'sr:sport:6': 6, // Handball
    'sr:sport:20': 7 // Table Tennis
  };

  const sportTabs = Object.entries(sportCounts)
    .filter(([id]) => id in SPORT_ORDER_BY_ID)
    .sort((a, b) => (SPORT_ORDER_BY_ID[a[0]] ?? 999) - (SPORT_ORDER_BY_ID[b[0]] ?? 999))
    .slice(0, 5)
    .map(([id, count]) => ({ id, name: sportNames[id], count }));

  const dynamicSports = useMemo(() => {
    const counts: Record<string, number> = {};
    const ids: Record<string, string> = {};

    if (fetchedTournaments.length > 0) {
      fetchedTournaments.forEach(t => {
        const s = t.sport_name || 'Soccer';
        counts[s] = (counts[s] || 0) + t.event_count;
        if (!ids[s] && t.sport_id) ids[s] = t.sport_id;
      });
      return Object.entries(counts).map(([sport_name, event_count]) => ({
        sport_name,
        sport_id: ids[sport_name],
        event_count
      }));
    }

    preEvents.forEach(ev => {
      const s = ev.event.sport_name;
      counts[s] = (counts[s] || 0) + 1;
      if (!ids[s]) ids[s] = ev.event.sport_id;
    });
    return Object.entries(counts).map(([sport_name, event_count]) => ({
      sport_name,
      sport_id: ids[sport_name],
      event_count
    }));
  }, [preEvents, fetchedTournaments]);

  // Nested tree for sidebar: Sport -> Country -> Tournament -> Count
  const sportsTree = useMemo(() => {
    const tree: Record<string, Record<string, Record<string, { count: number, id: string, categoryId: string }>>> = {};

    if (fetchedTournaments.length > 0) {
      fetchedTournaments.forEach((t) => {
        const s = t.sport_name || 'Soccer';
        const c = t.category_name || 'Other';
        const tName = t.tournament_name || 'Other';
        if (!tree[s]) tree[s] = {};
        if (!tree[s][c]) tree[s][c] = {};
        if (!tree[s][c][tName]) {
          tree[s][c][tName] = { count: 0, id: t.tournament_id, categoryId: t.category_id || '' };
        }
        tree[s][c][tName].count += t.event_count;
      });
      return tree;
    }

    preEvents.forEach((ev) => {
      const s = ev.event.sport_name;
      const c = ev.event.category_name || 'Other';
      const t = ev.event.tournament_name || 'Other';
      if (!tree[s]) tree[s] = {};
      if (!tree[s][c]) tree[s][c] = {};
      if (!tree[s][c][t]) {
        tree[s][c][t] = { count: 0, id: ev.event.tournament_id, categoryId: ev.event.category_id };
      }
      tree[s][c][t].count += 1;
    });
    return tree;
  }, [preEvents, fetchedTournaments]);

  // Dinamik Top Turnuvalar (Soccer) - Son 48 saate göre
  const dynamicTopCompetitions = useMemo(() => {
    if (fetchedTournaments.length > 0) {
      return fetchedTournaments
        .filter(t => (t.sport_name === 'Soccer' || t.sport_id === 'sr:sport:1'))
        .map(t => ({
          name: t.tournament_name,
          count: t.event_count,
          category: t.category_name || '',
          id: t.tournament_id
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 6);
    }

    const list: { name: string; count: number; category: string; id: string }[] = [];
    preEvents.forEach((ev) => {
      if (ev.event.sport_id === 'sr:sport:1') {
        const existing = list.find(l => l.id === ev.event.tournament_id);
        if (existing) {
          existing.count++;
        } else {
          list.push({ name: ev.event.tournament_name, count: 1, category: ev.event.category_name, id: ev.event.tournament_id });
        }
      }
    });
    return list.sort((a, b) => b.count - a.count).slice(0, 6);
  }, [preEvents, fetchedTournaments]);

  // Dinamik Top Ülkeler/Kategoriler (Soccer) - Son 48 saate göre
  const dynamicTopCategories = useMemo(() => {
    if (fetchedTournaments.length > 0) {
      const catMap: Record<string, number> = {};
      fetchedTournaments.forEach(t => {
        if (t.sport_name === 'Soccer' || t.sport_id === 'sr:sport:1') {
          const cat = t.category_name || 'Other';
          catMap[cat] = (catMap[cat] || 0) + t.event_count;
        }
      });
      return Object.entries(catMap)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 2);
    }

    const list: { name: string; count: number }[] = [];
    preEvents.forEach((ev) => {
      if (ev.event.sport_id === 'sr:sport:1') {
        const existing = list.find(l => l.name === ev.event.category_name);
        if (existing) {
          existing.count++;
        } else {
          list.push({ name: ev.event.category_name, count: 1 });
        }
      }
    });
    return list.sort((a, b) => b.count - a.count).slice(0, 2);
  }, [preEvents, fetchedTournaments]);



  // All live sport tabs (for live page)
  const allLiveSportTabs = [
    ...Object.entries(sportCounts)
      .sort((a, b) => (SPORT_ORDER_BY_ID[a[0]] ?? 999) - (SPORT_ORDER_BY_ID[b[0]] ?? 999))
      .map(([id, count]) => ({ id, name: sportNames[id], count, emoji: SPORT_EMOJI[sportNames[id]] || '🏅' }))
  ];

  // All pre sport tabs
  const preSportCounts: Record<string, number> = {};
  const preSportNames: Record<string, string> = {};
  for (const ev of preEvents) {
    const sId = ev.event.sport_id;
    const sName = ev.event.sport_name;
    if (sId && sName) {
      preSportCounts[sId] = (preSportCounts[sId] || 0) + 1;
      preSportNames[sId] = sName;
    }
  }
  const allPreSportTabs = Object.entries(preSportCounts)
    .filter(([id]) => id in SPORT_ORDER_BY_ID)
    .sort((a, b) => (SPORT_ORDER_BY_ID[a[0]] ?? 999) - (SPORT_ORDER_BY_ID[b[0]] ?? 999))
    .slice(0, 15)
    .map(([id, count]) => ({ id, name: preSportNames[id], count, emoji: SPORT_EMOJI[preSportNames[id]] || '🏅' }));

  const filteredLive = quickFilter === 'favorites' || activeLiveSport === 'Favorites'
    ? liveEvents.filter((ev) => isFavorite(ev.event_id)).slice(0, 60)
    : liveEvents.filter((ev) => ev.event.sport_id === activeLiveSport).slice(0, 60);

  const filteredPre = (() => {
    if (fetchedCompetitionEvents !== null) {
      let result = fetchedCompetitionEvents;
      if (timeFilter === 'heute') {
        const todayStr = new Date().toDateString();
        result = result.filter((e) => {
          const starts = e.event?.scheduled_at ?? e.event?.start_time;
          return starts && new Date(starts).toDateString() === todayStr;
        });
      } else if (timeFilter === '3std') {
        result = result.filter((e) => isWithinHours(e, 3));
      }
      return result;
    }

    // If specific filters are applied, use the full preEvents data so all matches can be found
    const hasFilter = (quickFilter !== null && quickFilter !== 'todays-matches' && quickFilter !== 'top-events') || selectedTournaments.size > 0;
    const sourceEvents = preEvents;

    if (quickFilter === 'favorites') {
      return sourceEvents.filter((ev) => isFavorite(ev.event_id));
    }

    if (selectedTournaments.size > 0) {
      return sourceEvents.filter((ev) => selectedTournaments.has(ev.event.tournament_name) || selectedTournaments.has(ev.event.tournament_id));
    }

    if (quickFilter === 'todays-matches') {
      const todayStr = new Date().toDateString();
      return sourceEvents.filter((e) => {
        const starts = e.event?.scheduled_at ?? e.event?.start_time;
        if (!starts) return false;
        return e.event?.sport_id === activeSidebarSport && new Date(starts).toDateString() === todayStr;
      });
    }

    if (quickFilter?.startsWith('category:')) {
      const catName = quickFilter.substring('category:'.length);
      return sourceEvents.filter((e) => e.event?.sport_id === activeSidebarSport && e.event?.category_name === catName);
    }

    if (quickFilter?.startsWith('tournament:')) {
      const trnName = quickFilter.substring('tournament:'.length);
      const trnId = TOURNAMENT_ID_MAP[trnName] || trnName;

      return sourceEvents.filter((e) =>
        e.event?.tournament_id === trnId ||
        (e.event?.sport_id === activeSidebarSport && e.event?.tournament_name === trnName)
      );
    }

    if (quickFilter === 'top-events') {
      return topEventsPageData.map(tItem => {
        const pm = tItem.primary_market;
        const mockMarket: Market = {
          market_id: pm?.market_id || '',
          canonical_key: pm?.canonical_key || '',
          name: pm?.market_key || '',
          specifiers: pm?.specifiers,
          outcomes: pm?.outcomes || [],
          status: 1,
          favourite: false
        };
        const snapshot: EventSnapshot = {
          event_id: tItem.event_id,
          event: tItem.event,
          markets: [mockMarket],
          scope: 'pre',
          version: 1,
          status: { home_score: 0, away_score: 0 }
        };
        return snapshot;
      });
    }

    let base = sourceEvents.filter((ev) => ev.event.sport_id === activePreSport);

    if (timeFilter === 'heute') {
      const todayStr = new Date().toDateString();
      return base.filter((e) => {
        const starts = e.event?.scheduled_at ?? e.event?.start_time;
        if (!starts) return false;
        return new Date(starts).toDateString() === todayStr;
      });
    }
    if (timeFilter === '3std') return base.filter((e) => isWithinHours(e, 3));
    if (timeFilter === '48std') return base.filter((e) => isWithinHours(e, 48));
    return base.slice(0, 60);
  })();

  const isSelected = useCallback((eventId: string, marketId: string, outcomeId: string, specifiers?: Record<string, string>) => {
    const baseId = generateBetItemId(eventId, marketId, outcomeId, specifiers);
    return bets.some((b) => b.id === baseId || b.id === `desk-${baseId}`);
  }, [bets]);

  const handleOddsClick = useCallback((ev: EventSnapshot, market: Market, outcome: Outcome, tippLabel: string) => {
    const baseId = generateBetItemId(ev.event_id, market.market_id, outcome.outcome_id, market.specifiers);
    if (isSelected(ev.event_id, market.market_id, outcome.outcome_id, market.specifiers)) {
      const existingBet = bets.find(b => b.id === baseId || b.id === `desk-${baseId}`);
      if (existingBet) onRemoveBet(existingBet.id);
    } else {
      const id = baseId;
      onAddBet({
        id,
        matchId: ev.event_id,
        matchName: `${ev.event.home.name} - ${ev.event.away.name}`,
        betType: outcome.outcome_id === '1' ? 'home' : outcome.outcome_id === '3' ? 'away' : 'draw',
        tippLabel,
        odds: formatOdds(outcome.odds),
        eventId: ev.event_id,
        marketId: market.market_id,
        outcomeId: outcome.outcome_id,
        outcomeName: outcome.name,
        specifiers: market.specifiers
          ? typeof market.specifiers === 'string'
            ? market.specifiers
            : Object.entries(market.specifiers).map(([k, v]) => `${k}=${v}`).join('|')
          : '',
      });
    }
  }, [isSelected, onRemoveBet, onAddBet]);

  // Add a Top MatchKombi card to the slip as one provider-priced CustomBet group
  // (operator "all"), mirroring the Bet Builder+ flow in MatchDetailPage.
  // The odds box on an MK+ card toggles the combo in the slip like a normal odds
  // button: click to add, click again to remove.
  const handleAddMatchKombi = useCallback((item: TopMatchKombiItem) => {
    const id = `cb-${item.combination_id}`;
    if (bets.some(b => b.id === id)) { onRemoveBet(id); return; }
    if ((item.selections ?? []).length === 0) return;
    const bi = buildMatchKombiBetItem(item);
    // Only one CustomBet per event can be priced at a time — replace any existing one.
    bets.filter(b => b.isCustomBet && b.eventId === item.event_id && b.id !== id).forEach(b => onRemoveBet(b.id));
    onAddBet(bi);
  }, [bets, onAddBet, onRemoveBet]);

  const isMatchKombiSelected = useCallback(
    (item: TopMatchKombiItem) => bets.some(b => b.id === `cb-${item.combination_id}`),
    [bets]
  );

  // The "MK+ erstellen" button is two-step on desktop, where the card stays
  // visible next to the detail panel: the first click opens the event detail on
  // the Bet Builder+ tab; a second click (while that event's detail is open)
  // drops the card's suggested combo into the slip so the odds box lights up.
  const handleOpenMatchKombiBuilder = useCallback(async (item: TopMatchKombiItem) => {
    if (selectedEvent?.event_id === item.event_id) {
      const id = `cb-${item.combination_id}`;
      if (!bets.some(b => b.id === id) && (item.selections ?? []).length > 0) {
        const bi = buildMatchKombiBetItem(item);
        bets.filter(b => b.isCustomBet && b.eventId === item.event_id && b.id !== id).forEach(b => onRemoveBet(b.id));
        onAddBet(bi);
      }
      return;
    }
    try {
      const ev = await getEvent(item.event_id);
      if (ev && ev.event_id) {
        setDetailInitialTab('builder');
        setSelectedEvent(ev);
      }
    } catch { /* ignore — detail simply won't open */ }
  }, [selectedEvent, bets, onAddBet, onRemoveBet]);

  // Add a Top Combi to the slip as a NORMAL accumulator: each leg is an ordinary
  // flat selection (operator "any" via buildEventGroups) — not a CustomBet group.
  const handleAddCombi = useCallback((item: TopCombiItem) => {
    buildCombiBetItems(item).forEach((bi) => {
      if (!bets.some(b => b.id === bi.id)) onAddBet(bi);
    });
  }, [bets, onAddBet]);

  const renderMarketColHeader = (type: string | number, width: string) => {
    const layoutType = getLayoutGroupForMarketId(type);
    if (layoutType === 'Tipp' || layoutType === '1. Halbzeit' || layoutType === 'Tipp Restzeit' || layoutType === '1.Hz Tipp Restzeit' || layoutType === 'Viertel 1x2') {
      return (
        <div className={`${width} mx-1 flex`}>
          <div className="flex-1 text-center">1</div>
          <div className="flex-1 text-center">X</div>
          <div className="flex-1 text-center">2</div>
        </div>
      );
    }
    if (layoutType === 'Sieger' || layoutType === 'Satz Sieger' || layoutType === 'Head-To-Head' || layoutType === 'Verlängerung Ja/Nein') {
      return (
        <div className={`${width} mx-1 flex`}>
          <div className="flex-1 text-center">1</div>
          <div className="flex-1 text-center">2</div>
        </div>
      );
    }
    if (layoutType.includes('Handicap')) {
      const is3Way = upcomingSport === 'sr:sport:1' || (activePage === 'live' && activeLiveSport === 'sr:sport:1');
      return (
        <div className={`${width} mx-1 flex`}>
          <div className="w-[32px]"></div>
          <div className="flex-1 text-center">1</div>
          {is3Way && <div className="flex-1 text-center">X</div>}
          <div className="flex-1 text-center">2</div>
        </div>
      );
    }
    if (layoutType.includes('Doppelte Chance')) {
      return (
        <div className={`${width} mx-1 flex`}>
          <div className="flex-1 text-center">1X</div>
          <div className="flex-1 text-center">12</div>
          <div className="flex-1 text-center">X2</div>
        </div>
      );
    }
    if (layoutType === 'Beide Teams treffen') {
      return (
        <div className={`${width} mx-1 flex`}>
          <div className="flex-1 text-center">Ja</div>
          <div className="flex-1 text-center">Nein</div>
        </div>
      );
    }
    if (layoutType.includes('Über/Unter')) {
      return (
        <div className={`${width} mx-1 flex`}>
          <div className="w-[32px]"></div>
          <div className="flex-1 text-center">+</div>
          <div className="flex-1 text-center">-</div>
        </div>
      );
    }
    if (layoutType.includes('Gerade/Ungerade')) {
      return (
        <div className={`${width} mx-1 flex`}>
          <div className="flex-1 text-center">Ger</div>
          <div className="flex-1 text-center">Ung</div>
        </div>
      );
    }
    if (layoutType === 'Nächstes Tor') {
      return (
        <div className={`${width} mx-1 flex`}>
          <div className="flex-1 text-center">1</div>
          <div className="flex-1 text-center">Kein</div>
          <div className="flex-1 text-center">2</div>
        </div>
      );
    }
    return <div className={`${width} mx-1 flex`}><div className="flex-1 text-center">-</div></div>;
  };

  const renderMarketColCells = useCallback((ev: EventSnapshot, rawType: string | number, width: string, isUp: boolean = false, isDown: boolean = false) => {
    const type = getLayoutGroupForMarketId(rawType);
    const btnClass = (sel: boolean, extraTextClass: string = 'text-gray-900') =>
      `flex-1 border border-[#c0c5cc] rounded-[4px] h-[35px] cursor-pointer text-center leading-[35px] text-[14px] font-bold mx-[2px] overflow-hidden p-0 relative transition flex items-center justify-center ${sel ? 'bg-[#333] border-[#333] text-white' : `bg-[#e3e6eb] hover:bg-[#d8dce0] ${extraTextClass}`}`;
    const emptyBox = () => <div className="flex-1 border border-gray-200 rounded-[4px] bg-gray-50 opacity-40 h-[35px] mx-[2px] overflow-hidden p-0 relative flex items-center justify-center cursor-not-allowed"><LockIcon /></div>;
    const hasSubRows = ev.scope !== 'pre' && ev.event.sport_id === 'sr:sport:1';
    const alignmentClass = hasSubRows ? 'justify-start' : 'justify-center';

    if (type === 'Tipp' || type === 'Tipp Restzeit' || type === '1.Hz Tipp Restzeit' || type === 'Viertel 1x2') {
      let odds3way: { home: any; draw: any; away: any; market: any } | null = null;
      if (type === 'Tipp Restzeit') odds3way = findRestzeitGeneric(ev.markets || []);
      else if (type === '1.Hz Tipp Restzeit') odds3way = find1stHalfRestzeitGeneric(ev.markets || []);
      else if (type === 'Viertel 1x2') odds3way = findQuarter1x2(ev.markets || []);
      else odds3way = find1x2Market(ev.markets || []);
      let subRowOdds: { home: Outcome; draw: Outcome; away: Outcome; market: Market } | null = null;
      let tippLabel1 = '', tippLabelX = '', tippLabel2 = '';
      if (ev.scope !== 'pre' && ev.event.sport_id === 'sr:sport:1') {
        if (type === 'Tipp') {
          subRowOdds = find1stHalfMarket(ev.markets || []);
          tippLabel1 = '1.Hz Heimsieg'; tippLabelX = '1.Hz Unentschieden'; tippLabel2 = '1.Hz Auswärtssieg';
        } else if (type === 'Tipp Restzeit' || type === '1.Hz Tipp Restzeit') {
          subRowOdds = find1stHalfRestzeitGeneric(ev.markets || []);
          tippLabel1 = '1.Hz Heimsieg Restzeit'; tippLabelX = '1.Hz Unent. Restzeit'; tippLabel2 = '1.Hz Auswärtssieg Restzeit';
        }
      }

      return (
        <div className={`${width} flex flex-col ${alignmentClass} flex-shrink-0 mx-1`}>
          <div className="flex items-center">
            {odds3way ? (
              ([
                { label: '1', outcome: odds3way.home, tipp: type === 'Tipp Restzeit' ? 'Heimsieg Restzeit' : type === '1.Hz Tipp Restzeit' ? '1.Hz Heimsieg Restzeit' : 'Heimsieg' },
                { label: 'X', outcome: odds3way.draw, tipp: type === 'Tipp Restzeit' ? 'Unent. Restzeit' : type === '1.Hz Tipp Restzeit' ? '1.Hz Unent. Restzeit' : 'Unentschieden' },
                { label: '2', outcome: odds3way.away, tipp: type === 'Tipp Restzeit' ? 'Auswärtssieg Restzeit' : type === '1.Hz Tipp Restzeit' ? '1.Hz Auswärts Restzeit' : 'Auswärtssieg' },
              ] as const).map(({ label, outcome, tipp }, i) => {
                const sel = isSelected(ev.event_id, odds3way!.market.market_id, outcome.outcome_id, odds3way!.market.specifiers);
                const isU = isUp && i === 1; // mock live tick
                const isD = isDown && i === 2; // mock live tick
                const textColor = isU ? 'text-[#00a650]' : isD ? 'text-[#cc0000]' : 'text-gray-800';
                return (
                  <OddsButton odds={outcome.odds} selected={sel || false} key={label} onClick={() => handleOddsClick(ev, odds3way!.market, outcome, tipp)}
                    className={btnClass(sel, textColor)}>
                    {(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}
                  </OddsButton>
                );
              })
            ) : (
              <>
                {emptyBox()}{emptyBox()}{emptyBox()}
              </>
            )}
          </div>
          {subRowOdds && (
            <div className="flex items-center mt-1">
              {([
                { label: '1', outcome: subRowOdds.home, tipp: tippLabel1 },
                { label: 'X', outcome: subRowOdds.draw, tipp: tippLabelX },
                { label: '2', outcome: subRowOdds.away, tipp: tippLabel2 },
              ] as const).map(({ label, outcome, tipp }) => {
                const sel = isSelected(ev.event_id, subRowOdds!.market.market_id, outcome.outcome_id, subRowOdds!.market.specifiers);
                return (
                  <OddsButton odds={outcome.odds} selected={sel || false} key={`fh-${label}`} onClick={() => handleOddsClick(ev, subRowOdds!.market, outcome, tipp)}
                    className={btnClass(sel)}>
                    {(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}
                  </OddsButton>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    if (type === '1. Halbzeit') {
      const fh = find1stHalfMarket(ev.markets || []);
      return (
        <div className={`${width} flex flex-col ${alignmentClass} flex-shrink-0 mx-1`}>
          <div className="flex items-center">
            {fh ? (
              ([
                { label: '1', outcome: fh.home, tipp: '1.Hz Heimsieg' },
                { label: 'X', outcome: fh.draw, tipp: '1.Hz Unentschieden' },
                { label: '2', outcome: fh.away, tipp: '1.Hz Auswärtssieg' },
              ] as const).map(({ label, outcome, tipp }) => {
                const sel = isSelected(ev.event_id, fh.market.market_id, outcome.outcome_id, fh.market.specifiers);

                return (
                  <OddsButton odds={outcome.odds} selected={sel || false} key={label} onClick={() => handleOddsClick(ev, fh.market, outcome, tipp)}
                    className={btnClass(sel)}>
                    {(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}
                  </OddsButton>
                );
              })
            ) : (
              <>
                {emptyBox()}{emptyBox()}{emptyBox()}
              </>
            )}
          </div>
        </div>
      );
    }
    if (type === 'Über/Unter' || type.includes('Über/Unter') || type.includes('Toplam Sayı Alt/Üst')) {
      let ou: { over: any; under: any; market: any } | null = null;
      if (type === 'Heim Über/Unter') {
        ou = findTeamTotal(ev.markets || [], true);
      } else if (type === 'Auswärts Über/Unter') {
        ou = findTeamTotal(ev.markets || [], false);
      } else if (type === '1.Hz Heim Über/Unter') {
        ou = findTeamTotal(ev.markets || [], true, true);
      } else if (type === '1.Hz Auswärts Über/Unter') {
        ou = findTeamTotal(ev.markets || [], false, true);
      } else if (type === 'Viertel Über/Unter') {
        ou = findQuarterTotal(ev.markets || []);
      } else if (type === '2. Halbzeit Über/Unter') {
        ou = find2ndHalfTotal(ev.markets || []);
      } else if (type === 'Toplam Sayı Alt/Üst') {
        ou = findPointsTotal(ev.markets || []);
      } else if (type === 'Set Toplam Sayı Alt/Üst') {
        ou = findSatzTotal(ev.markets || []);
      } else {
        ou = findOUGeneric(ev.markets || [], false, ev);
      }
      const fhOU = (ev.scope !== 'pre' && ev.event.sport_id === 'sr:sport:1' && type === 'Über/Unter') ? findOUGeneric(ev.markets || [], true, ev) : null;
      return (
        <div className={`${width} flex flex-col ${alignmentClass} flex-shrink-0 mx-1`}>
          <div className="flex items-center">
            {ou ? (
              <>
                <div className="w-[32px] text-center text-[11px] text-gray-400 font-bold truncate">
                  {getMarketAttr(ou!.market) || '2,5'}
                </div>
                {([
                  { label: '+', outcome: ou!.over, tipp: `Über ${getMarketAttr(ou!.market) || '2,5'}` },
                  { label: '−', outcome: ou!.under, tipp: `Unter ${getMarketAttr(ou!.market) || '2,5'}` },
                ] as const).map(({ label, outcome, tipp }) => {
                  const sel = isSelected(ev.event_id, ou!.market.market_id, outcome.outcome_id, ou!.market.specifiers);
                  return (
                    <OddsButton odds={outcome.odds} selected={sel || false} key={label} onClick={() => handleOddsClick(ev, ou!.market, outcome, tipp)}
                      className={btnClass(sel)}>
                      {(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}
                    </OddsButton>
                  );
                })}
              </>
            ) : (
              <>
                <div className="w-[32px]" />
                {emptyBox()}{emptyBox()}
              </>
            )}
          </div>
          {fhOU && (
            <div className="flex items-center mt-1">
              <div className="w-[32px] text-center text-[11px] text-gray-400 font-bold truncate">{getMarketAttr(fhOU.market) || '0,5'}</div>
              {([
                { label: '+', outcome: fhOU.over, tipp: `1.Hz Über ${getMarketAttr(fhOU.market) || '0,5'}` },
                { label: '−', outcome: fhOU.under, tipp: `1.Hz Unter ${getMarketAttr(fhOU.market) || '0,5'}` },
              ] as const).map(({ label, outcome, tipp }) => {
                const sel = isSelected(ev.event_id, fhOU.market.market_id, outcome.outcome_id, fhOU.market.specifiers);
                return (
                  <OddsButton odds={outcome.odds} selected={sel || false} key={`fh-${label}`} onClick={() => handleOddsClick(ev, fhOU.market, outcome, tipp)}
                    className={btnClass(sel)}>
                    {(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}
                  </OddsButton>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    if (type === 'Sieger' || type === 'Satz Sieger') {
      const sieger = type === 'Satz Sieger' ? findSatzSieger(ev.markets || []) : findH2H(ev.markets || []);
      return (
        <div className={`${width} flex flex-col ${alignmentClass} flex-shrink-0 mx-1`}>
          <div className="flex items-center">
            {sieger ? (
              ([
                { label: '1', outcome: sieger.home, tipp: type === 'Satz Sieger' ? 'Satz Sieger 1' : 'Sieger 1' },
                { label: '2', outcome: sieger.away, tipp: type === 'Satz Sieger' ? 'Satz Sieger 2' : 'Sieger 2' },
              ] as const).map(({ label, outcome, tipp }) => {
                const sel = isSelected(ev.event_id, sieger.market.market_id, outcome.outcome_id, sieger.market.specifiers);
                return (
                  <OddsButton odds={outcome.odds} selected={sel || false} key={label} onClick={() => handleOddsClick(ev, sieger.market, outcome, tipp)}
                    className={btnClass(sel)}>
                    {(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}
                  </OddsButton>
                );
              })
            ) : (
              <>{emptyBox()}{emptyBox()}</>
            )}
          </div>
        </div>
      );
    }
    if (type.includes('Handicap')) {
      const isSet = type === 'Satz-Handicap';
      let hc: { o1: any; o2: any; o3?: any; market: any } | null = null;
      if (type === '2. Halbzeit Handicap') hc = find2ndHalfHandicap(ev.markets || []);
      else if (type === 'Satz-Handicap') hc = findSatzHandicap(ev.markets || []);
      else if (type === '1.Hz Handicap') hc = findHandicap(ev.markets || [], true);
      else hc = findHandicap(ev.markets || []);

      const fhHC = (ev.scope !== 'pre' && ev.event.sport_id === 'sr:sport:1' && type === 'Handicap') ? findHandicap(ev.markets || [], true) : null;
      return (
        <div className={`${width} flex flex-col ${alignmentClass} flex-shrink-0 mx-1`}>
          <div className="flex items-center">
            {hc ? (
              <>
                <div className="w-[32px] text-center text-[10px] text-gray-400 font-bold truncate">
                  {getMarketAttr(hc!.market) || ''}
                </div>
                {hc!.o3 ? (
                  ([
                    { label: '1', outcome: hc!.o1, tipp: isSet ? 'Satz-Handicap 1' : 'Handicap 1' },
                    { label: 'X', outcome: hc!.o3, tipp: isSet ? 'Satz-Handicap X' : 'Handicap X' },
                    { label: '2', outcome: hc!.o2, tipp: isSet ? 'Satz-Handicap 2' : 'Handicap 2' },
                  ] as const).map(({ label, outcome, tipp }) => {
                    const sel = isSelected(ev.event_id, hc!.market.market_id, outcome.outcome_id, hc!.market.specifiers);
                    return (
                      <OddsButton odds={outcome.odds} selected={sel || false} key={label} onClick={() => handleOddsClick(ev, hc!.market, outcome, tipp)}
                        className={btnClass(sel)}>
                        {(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}
                      </OddsButton>
                    );
                  })
                ) : (
                  ([
                    { label: '1', outcome: hc!.o1, tipp: isSet ? 'Satz-Handicap 1' : 'Handicap 1' },
                    { label: '2', outcome: hc!.o2, tipp: isSet ? 'Satz-Handicap 2' : 'Handicap 2' },
                  ] as const).map(({ label, outcome, tipp }) => {
                    const sel = isSelected(ev.event_id, hc!.market.market_id, outcome.outcome_id, hc!.market.specifiers);
                    return (
                      <OddsButton odds={outcome.odds} selected={sel || false} key={label} onClick={() => handleOddsClick(ev, hc!.market, outcome, tipp)}
                        className={btnClass(sel)}>
                        {(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}
                      </OddsButton>
                    );
                  })
                )}
              </>
            ) : (
              <>
                <div className="w-[32px]" />
                {emptyBox()}{emptyBox()}
              </>
            )}
          </div>
          {fhHC && (
            <div className="flex items-center mt-1">
              <div className="w-[32px] text-center text-[10px] text-gray-400 font-bold truncate">
                {getMarketAttr(fhHC.market) || ''}
              </div>
              {fhHC.o3 ? (
                ([
                  { label: '1', outcome: fhHC.o1, tipp: '1.Hz Handicap 1' },
                  { label: 'X', outcome: fhHC.o3, tipp: '1.Hz Handicap X' },
                  { label: '2', outcome: fhHC.o2, tipp: '1.Hz Handicap 2' },
                ] as const).map(({ label, outcome, tipp }) => {
                  const sel = isSelected(ev.event_id, fhHC.market.market_id, outcome.outcome_id, fhHC.market.specifiers);
                  return (
                    <OddsButton odds={outcome.odds} selected={sel || false} key={`fh-${label}`} onClick={() => handleOddsClick(ev, fhHC.market, outcome, tipp)}
                      className={btnClass(sel)}>
                      {(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}
                    </OddsButton>
                  );
                })
              ) : (
                ([
                  { label: '1', outcome: fhHC.o1, tipp: '1.Hz Handicap 1' },
                  { label: '2', outcome: fhHC.o2, tipp: '1.Hz Handicap 2' },
                ] as const).map(({ label, outcome, tipp }) => {
                  const sel = isSelected(ev.event_id, fhHC.market.market_id, outcome.outcome_id, fhHC.market.specifiers);
                  return (
                    <OddsButton odds={outcome.odds} selected={sel || false} key={`fh-${label}`} onClick={() => handleOddsClick(ev, fhHC.market, outcome, tipp)}
                      className={btnClass(sel)}>
                      {(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}
                    </OddsButton>
                  );
                })
              )}
            </div>
          )}
        </div>
      );
    }
    if (type.includes('Doppelte Chance')) {
      const dc = findDCGeneric(ev.markets || [], type === '1.Hz Doppelte Chance');
      const fhDC = (ev.scope !== 'pre' && ev.event.sport_id === 'sr:sport:1' && type === 'Doppelte Chance') ? findDCGeneric(ev.markets || [], true) : null;
      return (
        <div className={`${width} flex flex-col ${alignmentClass} flex-shrink-0 mx-1`}>
          <div className="flex items-center">
            {dc ? (
              ([
                { label: '1X', outcome: dc.o1x, tipp: '1X' },
                { label: '12', outcome: dc.o12, tipp: '12' },
                { label: 'X2', outcome: dc.ox2, tipp: 'X2' },
              ] as const).map(({ label, outcome, tipp }) => {
                const sel = isSelected(ev.event_id, dc.market.market_id, outcome.outcome_id, dc.market.specifiers);
                return (
                  <OddsButton odds={outcome.odds} selected={sel || false} key={label} onClick={() => handleOddsClick(ev, dc.market, outcome, tipp)}
                    className={btnClass(sel)}>
                    {(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}
                  </OddsButton>
                );
              })
            ) : (
              <>{emptyBox()}{emptyBox()}{emptyBox()}</>
            )}
          </div>
          {fhDC && (
            <div className="flex items-center mt-1">
              {([
                { label: '1X', outcome: fhDC.o1x, tipp: '1.Hz 1X' },
                { label: '12', outcome: fhDC.o12, tipp: '1.Hz 12' },
                { label: 'X2', outcome: fhDC.ox2, tipp: '1.Hz X2' },
              ] as const).map(({ label, outcome, tipp }) => {
                const sel = isSelected(ev.event_id, fhDC.market.market_id, outcome.outcome_id, fhDC.market.specifiers);
                return (
                  <OddsButton odds={outcome.odds} selected={sel || false} key={`fh-${label}`} onClick={() => handleOddsClick(ev, fhDC.market, outcome, tipp)}
                    className={btnClass(sel)}>
                    {(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}
                  </OddsButton>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    if (type === 'Beide Teams treffen' || type === 'Verlängerung Ja/Nein') {
      const btts = type === 'Verlängerung Ja/Nein' ? findOvertimeYesNo(ev.markets || []) : findBTTSGeneric(ev.markets || []);
      const fhBTTS = (ev.scope !== 'pre' && ev.event.sport_id === 'sr:sport:1' && type === 'Beide Teams treffen') ? findBTTSGeneric(ev.markets || [], true) : null;
      return (
        <div className={`${width} flex flex-col ${alignmentClass} flex-shrink-0 mx-1`}>
          <div className="flex items-center">
            {btts ? (
              ([
                { label: 'Ja', outcome: btts.yes, tipp: 'Ja' },
                { label: 'Nein', outcome: btts.no, tipp: 'Nein' },
              ] as const).map(({ label, outcome, tipp }) => {
                const sel = isSelected(ev.event_id, btts.market.market_id, outcome.outcome_id, btts.market.specifiers);
                return (
                  <OddsButton odds={outcome.odds} selected={sel || false} key={label} onClick={() => handleOddsClick(ev, btts.market, outcome, tipp)}
                    className={btnClass(sel)}>
                    {(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}
                  </OddsButton>
                );
              })
            ) : <>{emptyBox()}</>}
          </div>
          {fhBTTS && (
            <div className="flex items-center mt-1">
              {([
                { label: 'Ja', outcome: fhBTTS.yes, tipp: '1.Hz Ja' },
                { label: 'Nein', outcome: fhBTTS.no, tipp: '1.Hz Nein' },
              ] as const).map(({ label, outcome, tipp }) => {
                const sel = isSelected(ev.event_id, fhBTTS.market.market_id, outcome.outcome_id, fhBTTS.market.specifiers);
                return (
                  <OddsButton odds={outcome.odds} selected={sel || false} key={`fh-${label}`} onClick={() => handleOddsClick(ev, fhBTTS.market, outcome, tipp)}
                    className={btnClass(sel)}>
                    {(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}
                  </OddsButton>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    if (type.includes('Gerade/Ungerade')) {
      const oddEven = findOddEven(ev.markets || [], type === '1.Hz Gerade/Ungerade');
      return (
        <div className={`${width} flex flex-col ${alignmentClass} flex-shrink-0 mx-1`}>
          <div className="flex items-center">
            {oddEven ? (
              ([
                { label: 'Tek', outcome: oddEven.odd, tipp: 'Tek' },
                { label: 'Çift', outcome: oddEven.even, tipp: 'Çift' },
              ] as const).map(({ label, outcome, tipp }) => {
                const sel = isSelected(ev.event_id, oddEven.market.market_id, outcome.outcome_id, oddEven.market.specifiers);
                return (
                  <OddsButton odds={outcome.odds} selected={sel || false} key={label} onClick={() => handleOddsClick(ev, oddEven.market, outcome, tipp)}
                    className={btnClass(sel)}>
                    {(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}
                  </OddsButton>
                );
              })
            ) : <>{emptyBox()}{emptyBox()}</>}
          </div>
        </div>
      );
    }
    if (type === 'Nächstes Tor') {
      const ng = findNextGoal(ev.markets || []);
      const fhForNextGoal = (ev.scope !== 'pre' && ev.event.sport_id === 'sr:sport:1') ? find1stHalfNextGoal(ev.markets || []) : null;
      return (
        <div className={`${width} flex flex-col ${alignmentClass} flex-shrink-0 mx-1`}>
          <div className="flex items-center">
            {ng ? (
              ([
                { label: '1', outcome: ng.home, tipp: 'Nächstes Tor Heim' },
                { label: 'Kein', outcome: ng.none, tipp: 'Kein Tor' },
                { label: '2', outcome: ng.away, tipp: 'Nächstes Tor Auswärts' },
              ] as const).map(({ label, outcome, tipp }) => {
                const sel = isSelected(ev.event_id, ng.market.market_id, outcome.outcome_id, ng.market.specifiers);
                return (
                  <OddsButton odds={outcome.odds} selected={sel || false} key={label} onClick={() => handleOddsClick(ev, ng.market, outcome, tipp)}
                    className={btnClass(sel)}>
                    {(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}
                  </OddsButton>
                );
              })
            ) : (
              <>{emptyBox()}{emptyBox()}{emptyBox()}</>
            )}
          </div>
          {fhForNextGoal && (
            <div className="flex items-center mt-1">
              {([
                { label: '1', outcome: fhForNextGoal.home, tipp: '1.Hz Nächstes Tor Heim' },
                { label: 'Kein', outcome: fhForNextGoal.none, tipp: '1.Hz Kein Tor' },
                { label: '2', outcome: fhForNextGoal.away, tipp: '1.Hz Nächstes Tor Auswärts' },
              ] as const).map(({ label, outcome, tipp }) => {
                const sel = isSelected(ev.event_id, fhForNextGoal.market.market_id, outcome.outcome_id, fhForNextGoal.market.specifiers);
                return (
                  <OddsButton odds={outcome.odds} selected={sel || false} key={`fhng-${label}`} onClick={() => handleOddsClick(ev, fhForNextGoal.market, outcome, tipp)}
                    className={btnClass(sel)}>
                    {(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}
                  </OddsButton>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    if (type === 'Draw No Bet' || type === 'Beraberlikte İade') {
      const dnb = findH2H(ev.markets || []);
      const fhDNB = (ev.scope !== 'pre' && ev.event.sport_id === 'sr:sport:1') ? findH2H(ev.markets || [], true) : null;
      return (
        <div className={`${width} flex flex-col ${alignmentClass} flex-shrink-0 mx-1`}>
          <div className="flex items-center">
            {dnb ? (
              ([
                { label: '1', outcome: dnb.home, tipp: 'DNB 1' },
                { label: '2', outcome: dnb.away, tipp: 'DNB 2' },
              ] as const).map(({ label, outcome, tipp }) => {
                const sel = isSelected(ev.event_id, dnb.market.market_id, outcome.outcome_id, dnb.market.specifiers);
                return (
                  <OddsButton odds={outcome.odds} selected={sel || false} key={label} onClick={() => handleOddsClick(ev, dnb.market, outcome, tipp)}
                    className={btnClass(sel)}>
                    {(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}
                  </OddsButton>
                );
              })
            ) : (
              <>{emptyBox()}{emptyBox()}</>
            )}
          </div>
          {fhDNB && (
            <div className="flex items-center mt-1">
              {([
                { label: '1', outcome: fhDNB.home, tipp: '1.Hz DNB 1' },
                { label: '2', outcome: fhDNB.away, tipp: '1.Hz DNB 2' },
              ] as const).map(({ label, outcome, tipp }) => {
                const sel = isSelected(ev.event_id, fhDNB.market.market_id, outcome.outcome_id, fhDNB.market.specifiers);
                return (
                  <OddsButton odds={outcome.odds} selected={sel || false} key={`fh-${label}`} onClick={() => handleOddsClick(ev, fhDNB.market, outcome, tipp)}
                    className={btnClass(sel)}>
                    {(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}
                  </OddsButton>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    // Mock empty boxes for other unmapped options
    return (
      <div className={`${width} flex flex-col ${alignmentClass} flex-shrink-0 mx-1`}>
        <div className="flex items-center">
          {emptyBox()}
        </div>
      </div>
    );
  }, [isSelected, handleOddsClick, t]);

  // Betting slip calculations
  const getIndividualStake = (betId: string) => individualStakes[betId] || stake;

  let totalStake = 0;
  if (betslipMode === 'single') {
    totalStake = bets.reduce((acc, bet) => acc + (parseFloat(getIndividualStake(bet.id)) || 0), 0);
  } else if (betslipMode === 'combi') {
    totalStake = parseFloat(stake) || 0;
  } else if (betslipMode === 'system') {
    const combCount = choose(bets.length, systemSize);
    totalStake = (parseFloat(stake) || 0) * combCount;
  }

  const stakeNum = totalStake;

  const { totalOdds: apiTotalOdds, possibleWin: apiPossibleWin, loading: quoteLoading, error: quoteError } = useBetSlipQuote(
    bets,
    betslipMode,
    stakeNum,
    systemSize,
    individualStakes
  );

  const combiOdds = bets.reduce((acc, bet) => {
    let oddVal = parseFloat(bet.odds.replace(',', '.'));
    if (isNaN(oddVal) || oddVal === 0 || bet.isLocked) oddVal = 1;
    return acc * oddVal;
  }, 1);

  const localTotalOddsStr = combiOdds.toFixed(2).replace('.', ',');
  const displayTotalOdds = (quoteLoading || quoteError || apiTotalOdds === '0,00') ? localTotalOddsStr : (
    <span className="flex items-center gap-2">
      {apiTotalOdds}
      {quoteLoading && <svg className="animate-spin h-3 w-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="10" strokeWidth="4" className="opacity-25"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg>}
    </span>
  );

  let localPossibleWin = '0,00';
  if (betslipMode === 'single') {
    const winSum = bets.reduce((acc, bet) => {
      const itemStake = parseFloat(getIndividualStake(bet.id)) || 0;
      const itemOdds = parseFloat(bet.odds.replace(',', '.')) || 0;
      return acc + (itemStake * itemOdds);
    }, 0);
    localPossibleWin = winSum.toFixed(2).replace('.', ',');
  } else if (betslipMode === 'combi') {
    const stakeVal = parseFloat(stake) || 0;
    localPossibleWin = (stakeVal * combiOdds).toFixed(2).replace('.', ',');
  } else if (betslipMode === 'system') {
    const stakePerComb = parseFloat(stake) || 0;
    const systemWin = calculateSystemPossibleWin(bets, systemSize, stakePerComb);
    localPossibleWin = systemWin.toFixed(2).replace('.', ',');
  }

  const possibleWin = (quoteLoading || quoteError || apiPossibleWin === '0,00') ? localPossibleWin : apiPossibleWin;

  const handlePlaceBet = async () => {
    const validBets = bets.filter((b) => b.eventId && b.marketId && b.outcomeId);
    if (!validBets.length) return;

    // Group by event for Acca/System (CustomBet support) using centralized helper
    const event_groups = buildEventGroups(validBets);

    setPlacing(true);
    setBetStatus('idle');
    try {
      const effectiveMode = (betslipMode === 'combi' && validBets.length === 1) ? 'single' : betslipMode;
      
      if (effectiveMode === 'single') {
        // Place each single bet in parallel
        for (const bet of validBets) {
          const itemStake = betslipMode === 'combi' ? (parseFloat(stake) || 0) : (parseFloat(getIndividualStake(bet.id)) || 0);
          if (itemStake <= 0) continue;
          await placeBet({
            ticket_type: 'single',
            stake: itemStake,
            selections: [{
              event_id: bet.eventId!,
              market_id: bet.marketId!,
              outcome_id: bet.outcomeId!,
              ...(bet.specifiers ? { specifiers: bet.specifiers } : {}),
            }]
          });
        }
      } else if (effectiveMode === 'combi') {
        // Place a combi bet (acca) with event_groups
        await placeBet({ ticket_type: 'acca', stake: parseFloat(stake) || 0, event_groups });
      } else if (effectiveMode === 'system') {
        // Place a system bet with native api
        const systemSelections = validBets.map(bet => ({
          event_id: bet.eventId!,
          market_id: bet.marketId!,
          outcome_id: bet.outcomeId!,
          specifiers: bet.specifiers,
        }));
        await placeBet({
          ticket_type: 'system',
          stake: stakeNum, // total stake for system
          selections: systemSelections,
          system: { combination_sizes: [systemSize] }
        });
      }
      setBetStatus('success');
      onClearAll();
      window.dispatchEvent(new Event('balance-changed'));
    } catch (err: any) {
      let msg = err.message || 'Fehler beim Platzieren.';
      if (msg.toLowerCase().includes('custom bet returned invalid odds') || msg.toLowerCase().includes('invalid odds')) {
        msg = "Seçtiğiniz bahisler Aynı Maç Kombinesi için birbiriyle uyumsuz (Örn: 1x2 ve Sonraki Gol). Lütfen farklı seçenekler deneyin.";
      } else if (msg.toLowerCase().includes('custombet calculation is currently unavailable')) {
        msg = "Aynı Maç Kombinesi (Bet Builder) hesaplaması şu anda sağlayıcı tarafından kullanılamıyor. Lütfen farklı maçlar seçerek kombine yapmayı deneyin.";
      } else if (msg.toLowerCase().includes('payout_limit_exceeded')) {
        msg = t('slip.error.payoutLimitExceeded');
      }
      setBetError(msg);
      setBetStatus('error');
    } finally {
      setPlacing(false);
    }
  };


  const [langMenuOpen, setLangMenuOpen] = useState(false);

  const desktopHeader = (
    <header className="sticky top-0 z-[100] shadow-md text-white" style={{ backgroundImage: 'var(--bg-brand-gradient-vertical)' }}>
      <div className="w-full mx-auto px-6 flex items-center h-[56px] gap-10">
        <button onClick={() => onNavigate('home')} className="header-alex-logo hover:opacity-80 transition cursor-pointer">
          <img src="/tip24beth.svg" alt="Tip24" className="h-8 object-contain" />
        </button>
        <nav className="flex gap-1 h-full">
          {[
            { id: 'home', label: t('nav.sports') },
            { id: 'live', label: t('nav.live') },
            { id: 'games', label: t('nav.games') },
            { id: 'aktionen', label: t('nav.promotions') },
          ].map((item) => {
            const isActive = activePage === item.id || (item.id === 'home' && ['home', 'top-events', 'todays-matches', 'german-football', 'european-football'].includes(activePage));
            return (
              <button
                key={item.id}
                onClick={() => item.id !== 'aktionen' && onNavigate(item.id)}
                className={`text-[13px] font-bold h-full px-4 transition uppercase tracking-wider flex items-center ${isActive
                  ? 'text-white border-b-4 border-white'
                  : 'text-white/80 border-b-4 border-transparent hover:text-white hover:bg-tipico-red-dark'
                  }`}
              >
                {item.label}
              </button>
            );
          })}
        </nav>
        <div className="ml-auto flex items-center gap-4 relative">
          <button onClick={toggleTheme} className="text-white hover:opacity-80 transition flex items-center justify-center p-2 rounded-full">
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>
          {isLogged ? (
            <>
              <div className="flex items-center gap-6">
                {/* Einzahlung Block */}
                <button className="flex flex-col items-center justify-center bg-black/20 hover:bg-black/30 rounded px-5 py-1.5 transition cursor-pointer">
                  <span className="text-[15px] text-white/90 leading-tight tracking-wide">{t('nav.deposit')}</span>
                  <span className="text-[14px] font-bold text-white leading-tight mt-0.5">
                    {wallet ? `${parseFloat(wallet.available_cash).toFixed(2)} €` : '0,00 €'}
                  </span>
                </button>

                {/* Header Icons */}
                <div className="flex items-center gap-6">
                  {/* Meine Wetten */}
                  <button onClick={() => onNavigate?.('bets')} className="flex flex-col items-center justify-center gap-[2px] text-white hover:text-white/80 transition cursor-pointer">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                    <span className="text-[15px] font-medium tracking-wide">{t('nav.myBets')}</span>
                  </button>



                  {/* Konto Button Toggle */}
                  <button
                    onClick={() => { setIsProfileOpen(false); onNavigate?.('account'); }}
                    className="flex flex-col items-center justify-center gap-[2px] text-white hover:text-white/80 transition cursor-pointer"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="text-[15px] font-medium tracking-wide">{t('nav.account')}</span>
                  </button>

                  {/* Language Switcher moved to Footer */}
                </div>
              </div>
            </>
          ) : (
            <>
              <button onClick={onLoginClick} className="text-white font-bold text-[15px] hover:opacity-80 transition">{t('nav.login')}</button>
              <button onClick={onRegisterClick} className="bg-white text-tipico-red px-6 py-2 rounded-full text-[15px] font-bold shadow-md hover:bg-gray-100 transition">{t('nav.register')}</button>
            </>
          )}
        </div>
      </div>
    </header>
  );

  let liveSportEvents: EventSnapshot[] = [];
  let sorted: EventSnapshot[] = [];
  let next6h: EventSnapshot[] = [];
  let tournamentGroups: Record<string, EventSnapshot[]> = {};

  // Desktop Live sayfasında "next6h" arrayi artık liveUpcomingEvents'ten alınacak
  if (activePage === 'live') {
    next6h = liveUpcomingEvents;
  } else {
    next6h = preEvents.filter((ev) => {
      const sportId = activePreSport;
      const sportMatch = sportId === 'Favorites' ? isFavorite(ev.event_id) : ev.event.sport_id === sportId;
      return sportMatch && isWithinHours(ev, 6);
    });
  }

  if (activePage === 'live') {
    liveSportEvents = activeLiveSport === 'Favorites'
      ? liveEvents.filter(ev => isFavorite(ev.event_id))
      : liveEvents.filter((ev) => ev.event.sport_id === activeLiveSport);
    if (sortBy === 'wettbewerb') {
      sorted = [...liveSportEvents].sort((a, b) => a.event.tournament_name.localeCompare(b.event.tournament_name));
    } else if (sortBy === 'oddsAsc') {
      sorted = [...liveSportEvents].sort((a, b) => {
        const aOdds = parseFloat(find1x2Market(a.markets || [])?.home?.odds ?? '999');
        const bOdds = parseFloat(find1x2Market(b.markets || [])?.home?.odds ?? '999');
        return aOdds - bOdds;
      });
    } else if (sortBy === 'oddsDesc') {
      sorted = [...liveSportEvents].sort((a, b) => {
        const aOdds = parseFloat(find1x2Market(a.markets || [])?.home?.odds ?? '0');
        const bOdds = parseFloat(find1x2Market(b.markets || [])?.home?.odds ?? '0');
        return bOdds - aOdds;
      });
    } else {
      sorted = [...liveSportEvents].sort((a, b) => {
        if (initialSortRef.current[a.event_id] === undefined) {
          initialSortRef.current[a.event_id] = parseInt(a.status?.clock?.split(':')[0] || '0');
        }
        if (initialSortRef.current[b.event_id] === undefined) {
          initialSortRef.current[b.event_id] = parseInt(b.status?.clock?.split(':')[0] || '0');
        }
        return initialSortRef.current[b.event_id] - initialSortRef.current[a.event_id];
      });
    }

    if (sortBy === 'wettbewerb') {
      for (const ev of sorted) {
        const t = ev.event.tournament_name;
        if (!tournamentGroups[t]) tournamentGroups[t] = [];
        tournamentGroups[t].push(ev);
      }
    }
  }

  const columnHeaders = (
    <>
      <div className="px-5 py-2 flex items-center bg-[#f7f8f9] border-b border-gray-200">
        {/* Sort button */}
        <button
          onClick={() => setSortModalOpen(true)}
          className="flex items-center justify-center w-8 h-8 bg-white border border-gray-200 rounded text-gray-500 hover:border-gray-400 hover:text-gray-900 transition shadow-sm flex-shrink-0"
          title="Sortiere nach"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 8l4-4 4 4M7 4v16" />
            <path d="M21 16l-4 4-4-4M17 20V4" />
          </svg>
        </button>
        <div className="flex-1" />
        <div className="relative">
          <div onClick={() => setOpenDropdown(o => o === 'cl1' ? null : 'cl1')} className="w-[151px] mx-1 flex-shrink-0 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
            <span className="text-[15px] font-bold text-gray-700 truncate">{translateMarketFilterLabel(liveMarket1, locale)}</span>
            <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </div>
          {openDropdown === 'cl1' && (
            <div className="absolute top-full left-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
              <div className="px-3 py-1.5 text-[15px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">Tipp</div>
              {getMarketOptionsForSport(activeLiveSport, true).map(opt => (
                <button key={opt} onClick={() => { setLiveMarket1(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[15px] hover:bg-gray-100 flex items-center gap-2">
                  <div className="w-4">{liveMarket1 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                  <span className={liveMarket1 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className={`relative${selectedEvent ? ' hidden min-[1500px]:block' : ''}`}>
          <div onClick={() => setOpenDropdown(o => o === 'cl2' ? null : 'cl2')} className="w-[151px] mx-1 flex-shrink-0 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
            <span className="text-[15px] font-bold text-gray-700 truncate">{translateMarketFilterLabel(liveMarket2, locale)}</span>
            <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </div>
          {openDropdown === 'cl2' && (
            <div className="absolute top-full left-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
              <div className="px-3 py-1.5 text-[15px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">Tipp</div>
              {getMarketOptionsForSport(activeLiveSport, true).map(opt => (
                <button key={opt} onClick={() => { setLiveMarket2(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[15px] hover:bg-gray-100 flex items-center gap-2">
                  <div className="w-4">{liveMarket2 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                  <span className={liveMarket2 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className={`relative ${selectedEvent ? 'hidden min-[1650px]:block' : 'hidden min-[1500px]:block'}`}>
          <div onClick={() => setOpenDropdown(o => o === 'cl3' ? null : 'cl3')} className="w-[151px] mx-1 flex-shrink-0 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
            <span className="text-[15px] font-bold text-gray-700 truncate">{translateMarketFilterLabel(liveMarket3, locale)}</span>
            <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </div>
          {openDropdown === 'cl3' && (
            <div className="absolute top-full left-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
              <div className="px-3 py-1.5 text-[15px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">Tipp</div>
              {getMarketOptionsForSport(activeLiveSport, true).map(opt => (
                <button key={opt} onClick={() => { setLiveMarket3(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[15px] hover:bg-gray-100 flex items-center gap-2">
                  <div className="w-4">{liveMarket3 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                  <span className={liveMarket3 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {!selectedEvent && (
          <div className="relative hidden min-[1650px]:block">
            <div onClick={() => setOpenDropdown(o => o === 'cl4' ? null : 'cl4')} className="w-[151px] mx-1 flex-shrink-0 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
              <span className="text-[15px] font-bold text-gray-700 truncate">{translateMarketFilterLabel(liveMarket4, locale)}</span>
              <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </div>
            {openDropdown === 'cl4' && (
              <div className="absolute top-full right-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                <div className="px-3 py-1.5 text-[15px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">Tipp</div>
                {getMarketOptionsForSport(activeLiveSport, true).map(opt => (
                  <button key={opt} onClick={() => { setLiveMarket4(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[15px] hover:bg-gray-100 flex items-center gap-2">
                    <div className="w-4">{liveMarket4 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                    <span className={liveMarket4 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {!selectedEvent && (
          <div className="relative hidden min-[1750px]:block">
            <div onClick={() => setOpenDropdown(o => o === 'cl5' ? null : 'cl5')} className="w-[151px] mx-1 flex-shrink-0 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
              <span className="text-[15px] font-bold text-gray-700 truncate">{translateMarketFilterLabel(liveMarket5, locale)}</span>
              <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </div>
            {openDropdown === 'cl5' && (
              <div className="absolute top-full right-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                <div className="px-3 py-1.5 text-[15px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">Tipp</div>
                {getMarketOptionsForSport(activeLiveSport, true).map(opt => (
                  <button key={opt} onClick={() => { setLiveMarket5(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[15px] hover:bg-gray-100 flex items-center gap-2">
                    <div className="w-4">{liveMarket5 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                    <span className={liveMarket5 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="w-[52px]" />
      </div>
      {/* Sort Modal */}
      {sortModalOpen && (
        <div className="fixed inset-0 flex items-end justify-center" style={{ zIndex: 130 }} onClick={() => setSortModalOpen(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative w-full max-w-md bg-white rounded-t-2xl shadow-2xl pointer-events-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="grid grid-cols-[auto_1fr_auto] items-center min-h-14 px-4 border-b border-gray-100">
              <div />
              <div className="text-center text-[17px] font-semibold text-gray-900">Sortiere nach:</div>
              <button onClick={() => setSortModalOpen(false)} className="text-gray-400 hover:text-gray-700 p-1">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <ul className="py-2">
              {([
                { key: 'zeit', label: 'Zeit' },
                { key: 'wettbewerb', label: 'Wettbewerb' },
                { key: 'oddsAsc', label: 'Quoten: Aufsteigend' },
                { key: 'oddsDesc', label: 'Quoten: Absteigend' },
              ] as const).map(({ key, label }) => (
                <li
                  key={key}
                  onClick={() => { setSortBy(key); setSortModalOpen(false); }}
                  className="flex items-center justify-between px-5 py-3 cursor-pointer hover:bg-gray-50 transition"
                >
                  <span className={`text-[16px] ${sortBy === key ? 'font-semibold text-gray-900' : 'font-normal text-gray-700'}`}>{label}</span>
                  {sortBy === key && (
                    <svg className="w-5 h-5 text-gray-900" viewBox="0 0 19 16" fill="currentColor">
                      <path d="M17.1573 0.435014C16.9627 0.679709 14.5718 3.69042 11.8443 7.12549C9.11677 10.5605 6.87729 13.3683 6.86769 13.3649C6.85812 13.3615 5.51622 12.0145 3.88574 10.3714L0.921208 7.38412L0.519965 7.77059C0.299288 7.98314 0.0920085 8.18521 0.0593511 8.21963L0 8.28218L2.61212 10.9124C4.04878 12.359 5.61959 13.9406 6.10279 14.427L6.98135 15.3115L12.7407 8.0584C15.9083 4.0692 18.5 0.797431 18.5 0.787812C18.5 0.774251 17.598 0.0438195 17.5273 0.000112908C17.5184 -0.00538105 17.3519 0.190318 17.1573 0.435014Z" />
                    </svg>
                  )}
                </li>
              ))}
            </ul>
            <div className="px-5 pb-24 pt-2">
              <button
                onClick={() => setSortModalOpen(false)}
                className="w-full h-12 bg-[#cc0000] hover:bg-[#aa0000] text-white text-[16px] font-semibold rounded-lg transition"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="px-5 py-1.5 flex items-center bg-[#f7f8f9] border-b border-gray-200 text-[15px] text-gray-500 font-medium">
        <div className="w-[48px]" />
        <div className="flex-1" />
        {renderMarketColHeader(liveMarket1, "w-[151px]")}
        <div className={`flex-shrink-0${selectedEvent ? ' hidden min-[1500px]:block' : ''}`}>
          {renderMarketColHeader(liveMarket2, "w-[151px]")}
        </div>
        <div className={`flex-shrink-0 ${selectedEvent ? 'hidden min-[1650px]:block' : 'hidden min-[1500px]:block'}`}>
          {renderMarketColHeader(liveMarket3, "w-[151px]")}
        </div>
        {!selectedEvent && (
          <div className="hidden min-[1650px]:block flex-shrink-0">
            {renderMarketColHeader(liveMarket4, "w-[151px]")}
          </div>
        )}
        {!selectedEvent && (
          <div className="hidden min-[1750px]:block flex-shrink-0">
            {renderMarketColHeader(liveMarket5, "w-[151px]")}
          </div>
        )}
        <div className="w-[52px]" />
      </div>
    </>
  );

  const upcomingColumnHeaders = (
    <>
      <div className="px-5 py-2 flex items-center bg-[#f7f8f9] border-b border-gray-200">
        <div className="flex-1" />
        <div className="relative">
          <div onClick={() => setOpenDropdown(o => o === 'uc1' ? null : 'uc1')} className="w-[151px] mx-1 flex-shrink-0 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
            <span className="text-[15px] font-bold text-gray-700 truncate">{translateMarketFilterLabel(upcomingMarket1, locale)}</span>
            <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </div>
          {openDropdown === 'uc1' && (
            <div className="absolute top-full left-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
              <div className="px-3 py-1.5 text-[15px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">Tipp</div>
              {getMarketOptionsForSport(upcomingSport, false).map(opt => (
                <button key={opt} onClick={() => { setUpcomingMarket1(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[15px] hover:bg-gray-100 flex items-center gap-2">
                  <div className="w-4">{upcomingMarket1 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                  <span className={upcomingMarket1 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className={`relative${selectedEvent ? ' hidden min-[1500px]:block' : ''}`}>
          <div onClick={() => setOpenDropdown(o => o === 'uc2' ? null : 'uc2')} className="w-[151px] mx-1 flex-shrink-0 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
            <span className="text-[15px] font-bold text-gray-700 truncate">{translateMarketFilterLabel(upcomingMarket2, locale)}</span>
            <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </div>
          {openDropdown === 'uc2' && (
            <div className="absolute top-full left-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
              <div className="px-3 py-1.5 text-[15px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">Tipp</div>
              {getMarketOptionsForSport(upcomingSport, false).map(opt => (
                <button key={opt} onClick={() => { setUpcomingMarket2(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[15px] hover:bg-gray-100 flex items-center gap-2">
                  <div className="w-4">{upcomingMarket2 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                  <span className={upcomingMarket2 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className={`relative ${selectedEvent ? 'hidden min-[1650px]:block' : 'hidden min-[1500px]:block'}`}>
          <div onClick={() => setOpenDropdown(o => o === 'uc3' ? null : 'uc3')} className="w-[151px] mx-1 flex-shrink-0 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
            <span className="text-[15px] font-bold text-gray-700 truncate">{translateMarketFilterLabel(upcomingMarket3, locale)}</span>
            <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </div>
          {openDropdown === 'uc3' && (
            <div className="absolute top-full left-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
              <div className="px-3 py-1.5 text-[15px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">Tipp</div>
              {getMarketOptionsForSport(upcomingSport, false).map(opt => (
                <button key={opt} onClick={() => { setUpcomingMarket3(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[15px] hover:bg-gray-100 flex items-center gap-2">
                  <div className="w-4">{upcomingMarket3 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                  <span className={upcomingMarket3 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {!selectedEvent && (
          <div className="relative hidden min-[1650px]:block flex-shrink-0">
            <div onClick={() => setOpenDropdown(o => o === 'uc4' ? null : 'uc4')} className="w-[151px] mx-1 flex-shrink-0 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
              <span className="text-[15px] font-bold text-gray-700 truncate">{translateMarketFilterLabel(upcomingMarket4, locale)}</span>
              <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </div>
            {openDropdown === 'uc4' && (
              <div className="absolute top-full left-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                <div className="px-3 py-1.5 text-[15px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">Tipp</div>
                {getMarketOptionsForSport(upcomingSport, false).map(opt => (
                  <button key={opt} onClick={() => { setUpcomingMarket4(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[15px] hover:bg-gray-100 flex items-center gap-2">
                    <div className="w-4">{upcomingMarket4 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                    <span className={upcomingMarket4 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {!selectedEvent && (
          <div className="relative hidden min-[1750px]:block flex-shrink-0">
            <div onClick={() => setOpenDropdown(o => o === 'uc5' ? null : 'uc5')} className="w-[151px] mx-1 flex-shrink-0 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
              <span className="text-[15px] font-bold text-gray-700 truncate">{translateMarketFilterLabel(upcomingMarket5, locale)}</span>
              <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </div>
            {openDropdown === 'uc5' && (
              <div className="absolute top-full left-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                <div className="px-3 py-1.5 text-[15px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">Tipp</div>
                {getMarketOptionsForSport(upcomingSport, false).map(opt => (
                  <button key={opt} onClick={() => { setUpcomingMarket5(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[15px] hover:bg-gray-100 flex items-center gap-2">
                    <div className="w-4">{upcomingMarket5 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                    <span className={upcomingMarket5 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="w-[52px]" />
      </div>
    </>
  );

  const renderCrest = useCallback((crestUrl?: string) => {
    if (crestUrl) return <img src={crestUrl} alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" />;
    return <img src="/default-crest.svg" alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" />;
  }, []);

  const hasSelectedEvent = !!selectedEvent;

  const renderLiveOddsRow = useCallback((ev: EventSnapshot) => {
    const total = ev.markets?.reduce((a, m) => a + m.outcomes.length, 0) ?? 0;
    const redHome = (ev.status?.statistics?.red_cards?.home || 0) > 0;
    const redAway = (ev.status?.statistics?.red_cards?.away || 0) > 0;

    const clockStr = formatClock(ev, t) || t('live.ht');
    const homeScore = ev.status?.home_score ?? 0;
    const awayScore = ev.status?.away_score ?? 0;

    const ps = ev.status?.period_scores;
    const firstHalf = ps?.find(p => p.number === 1);
    const clockMin = ev.status?.clock ? parseInt(ev.status.clock.split(':')[0], 10) : 0;
    const isSecondHalf = firstHalf ? (ps!.length >= 2 || clockMin > 45) : clockMin > 45;

    return (
      <div key={ev.event_id} className="px-5 py-1 flex items-start hover:bg-gray-50 transition border-b border-gray-200 bg-white last:border-b-0">
        {/* Clock */}
        <div className="w-[36px] flex-shrink-0 cursor-pointer pt-0.5 text-left border-r border-gray-100 pr-2" onClick={() => handleEventClick(ev)}>
          <div className="text-[13px] font-bold text-gray-900">{clockStr}</div>
          <div className="text-[9px] font-bold text-[#cc0000] tracking-widest mt-0.5">LIVE</div>
        </div>

        {/* Teams + Score */}
        <div className="flex-1 min-w-0 flex items-center cursor-pointer border-r border-gray-100 pr-4 pl-2 relative" onClick={() => handleEventClick(ev)}>
          <div className="flex-1 min-w-0 flex flex-col justify-center">
            <div className="flex items-center gap-2 min-w-0 h-[22px]">
              {renderCrest(ev.event.home.crest_url)}
              <span className="text-[15px] font-medium text-gray-900 truncate">{ev.event.home.name}</span>
              {redHome && <span className="w-1.5 h-2.5 bg-[#cc0000] rounded-sm ml-1 shadow-sm" />}
            </div>
            <div className="flex items-center gap-2 min-w-0 h-[22px]">
              {renderCrest(ev.event.away.crest_url)}
              <span className="text-[15px] font-medium text-gray-900 truncate">{ev.event.away.name}</span>
              {redAway && <span className="w-1.5 h-2.5 bg-[#cc0000] rounded-sm ml-1 shadow-sm" />}
            </div>
            {ev.event.sport_id === 'sr:sport:1' && find1stHalfMarket(ev.markets || []) && (
              <div className="text-[11px] font-bold text-[#D41534] mt-0.5 pl-6">{t('market.firstHalf')}</div>
            )}
          </div>

          {activeStreams.has(ev.event_id) && (
            <div className="mx-3 flex-shrink-0">
              <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
          )}

          <div className="flex flex-col justify-center flex-shrink-0 text-right">
            <div className="h-[22px] flex items-center justify-end gap-2.5">
              {isSecondHalf && firstHalf && (
                <span className="text-[14px] font-medium text-gray-400">({firstHalf.home_score})</span>
              )}
              <span className="text-[15px] font-bold text-[#0088cc]">{homeScore}</span>
            </div>
            <div className="h-[22px] flex items-center justify-end gap-2.5">
              {isSecondHalf && firstHalf && (
                <span className="text-[14px] font-medium text-gray-400">({firstHalf.away_score})</span>
              )}
              <span className="text-[15px] font-bold text-[#0088cc]">{awayScore}</span>
            </div>
          </div>
        </div>

        {/* Markets */}
        {renderMarketColCells(ev, liveMarket1, "w-[151px]")}
        <div className={`flex-shrink-0${hasSelectedEvent ? ' hidden min-[1500px]:block' : ''}`}>
          {renderMarketColCells(ev, liveMarket2, "w-[151px]")}
        </div>
        <div className={`flex-shrink-0 ${hasSelectedEvent ? 'hidden min-[1650px]:block' : 'hidden min-[1500px]:block'}`}>
          {renderMarketColCells(ev, liveMarket3, "w-[151px]")}
        </div>
        {!hasSelectedEvent && (
          <div className="hidden min-[1650px]:block flex-shrink-0">
            {renderMarketColCells(ev, liveMarket4, "w-[151px]")}
          </div>
        )}
        {!hasSelectedEvent && (
          <div className="hidden min-[1750px]:block flex-shrink-0">
            {renderMarketColCells(ev, liveMarket5, "w-[151px]")}
          </div>
        )}

        {/* Market count */}
        <button onClick={() => handleEventClick(ev)} className="w-[52px] flex items-start justify-end pt-2 text-[15px] font-medium text-gray-500 hover:text-gray-800 transition">
          <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
          {total} ›
        </button>
      </div>
    );
  }, [t, handleEventClick, renderCrest, renderMarketColCells, liveMarket1, liveMarket2, liveMarket3, liveMarket4, liveMarket5, hasSelectedEvent]);

  const renderNext6hRow = useCallback((ev: EventSnapshot) => {
    const total = ev.markets?.reduce((a, m) => a + m.outcomes.length, 0) ?? 0;
    const starts = ev.event?.scheduled_at ?? ev.event?.start_time;
    const timeStr = starts ? formatTime(new Date(starts).toISOString()) : '';

    return (
      <div key={ev.event_id} className="px-5 py-1 flex items-start hover:bg-gray-50 transition border-b border-gray-200 bg-white last:border-b-0">
        {/* Clock */}
        <div className="w-[36px] flex-shrink-0 cursor-pointer pt-0.5 text-left border-r border-gray-100 pr-2" onClick={() => handleEventClick(ev)}>
          <div className="text-[13px] font-medium text-gray-500">{timeStr}</div>
        </div>

        {/* Teams */}
        <div className="flex-1 min-w-0 flex flex-col cursor-pointer border-r border-gray-100 pr-4 pl-2 justify-center" onClick={() => handleEventClick(ev)}>
          <div className="flex items-center gap-2 min-w-0">
            {renderCrest(ev.event.home.crest_url)}
            <span className="text-[15px] font-medium text-gray-900 truncate">{ev.event.home.name}</span>
          </div>
          <div className="flex items-center gap-2 min-w-0">
            {renderCrest(ev.event.away.crest_url)}
            <span className="text-[15px] font-medium text-gray-900 truncate">{ev.event.away.name}</span>
          </div>
        </div>

        {/* Markets */}
        {renderMarketColCells(ev, upcomingMarket1, "w-[151px]")}
        <div className={`flex-shrink-0${hasSelectedEvent ? ' hidden min-[1500px]:block' : ''}`}>
          {renderMarketColCells(ev, upcomingMarket2, "w-[151px]")}
        </div>
        <div className={`flex-shrink-0 ${hasSelectedEvent ? 'hidden min-[1650px]:block' : 'hidden min-[1500px]:block'}`}>
          {renderMarketColCells(ev, upcomingMarket3, "w-[151px]")}
        </div>
        {!hasSelectedEvent && (
          <div className="hidden min-[1650px]:block flex-shrink-0">
            {renderMarketColCells(ev, upcomingMarket4, "w-[151px]")}
          </div>
        )}
        {!hasSelectedEvent && (
          <div className="hidden min-[1750px]:block flex-shrink-0">
            {renderMarketColCells(ev, upcomingMarket5, "w-[151px]")}
          </div>
        )}

        {/* Market count */}
        <button onClick={() => handleEventClick(ev)} className="w-[52px] flex items-start justify-end pt-2 text-[15px] font-medium text-gray-500 hover:text-gray-800 transition">
          <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
          {total} ›
        </button>
      </div>
    );
  }, [handleEventClick, renderCrest, renderMarketColCells, upcomingMarket1, upcomingMarket2, upcomingMarket3, upcomingMarket4, upcomingMarket5, hasSelectedEvent]);

  if (activePage === 'account') {
    return (
      <div className="min-h-screen bg-tipico-gray-light flex flex-col">
        {desktopHeader}
        <div className="flex-1 min-w-0 flex flex-col py-6">
          <DesktopAccountPage onLoginClick={onLoginClick} />
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${activePage === 'live' ? 'bg-[#f2f2f2]' : 'bg-tipico-gray-light'}`}>
      {desktopHeader}

      <div className={`${activePage === 'live' ? 'w-[99%] mx-auto px-6' : 'w-[99%] mx-auto'} py-6 flex gap-1 items-start`}>

        {activePage !== 'live' && activePage !== 'bets' && (
          <DesktopLeftSidebar
            activeSport={activeSidebarSport}
            setActiveSport={(s) => { setActiveSidebarSport(s); setActivePreSport(s); setActiveLiveSport(s); }}
            quickFilter={quickFilter}
            setQuickFilter={setQuickFilter}
            timeFilter={timeFilter}
            setTimeFilter={setTimeFilter}
            expandedTopSport={expandedTopSport}
            setExpandedTopSport={setExpandedTopSport}
            expandedAlleSport={expandedAlleSport}
            setExpandedAlleSport={setExpandedAlleSport}
            expandedCategory={expandedCategory}
            setExpandedCategory={setExpandedCategory}
            showAllCountries={showAllCountries}
            setShowAllCountries={setShowAllCountries}
            selectedTournaments={selectedTournaments}
            setSelectedTournaments={setSelectedTournaments}
            fetchedTournaments={fetchedTournaments}
            toggleTournament={toggleTournament}
            toggleCountry={toggleCountry}
            allEvents={allEventsForSearch}
            onAddBet={handleOddsClick}
            isSelected={isSelected}
            onEventClick={handleEventClick}
            onNavigate={onNavigate}
            onOpenSearchModal={() => setIsSearchModalOpen(true)}
          />
        )}

        <main className="flex-1 min-w-0 flex flex-col gap-6 transition-all">
          <DesktopCenterContent
            activeStreams={activeStreams}
            activePage={activePage} loading={loading || fetchingCompetition} filteredPre={filteredPre} filteredLive={filteredLive}
            topPreEvents={topPreEvents} topLiveEvents={topLiveEvents} topEventsApiData={topEventsApiData}
            topEventsSportTabs={topEventsSportTabs}
            activePreSport={activePreSport} setActivePreSport={setActivePreSport}
            activeLiveSport={activeLiveSport} setActiveLiveSport={setActiveLiveSport}
            allPreSportTabs={allPreSportTabs}
            onOddsClick={handleOddsClick}
            onAddMatchKombi={handleAddMatchKombi}
            onOpenMatchKombiBuilder={handleOpenMatchKombiBuilder}
            isMatchKombiSelected={isMatchKombiSelected}
            onAddCombi={handleAddCombi}
            isSelected={isSelected}
            quickFilter={quickFilter} setQuickFilter={setQuickFilter}
            topMarket1={topMarket1} setTopMarket1={setTopMarket1}
            topMarket2={topMarket2} setTopMarket2={setTopMarket2}
            topMarket3={topMarket3} setTopMarket3={setTopMarket3}
            topMarket4={topMarket4} setTopMarket4={setTopMarket4}
            topMarket5={topMarket5} setTopMarket5={setTopMarket5}
            lastMinMarket1={lastMinMarket1} setLastMinMarket1={setLastMinMarket1}
            lastMinMarket2={lastMinMarket2} setLastMinMarket2={setLastMinMarket2}
            lastMinMarket3={lastMinMarket3} setLastMinMarket3={setLastMinMarket3}
            lastMinMarket4={lastMinMarket4} setLastMinMarket4={setLastMinMarket4}
            lastMinMarket5={lastMinMarket5} setLastMinMarket5={setLastMinMarket5}
            liveMarket1={liveMarket1} setLiveMarket1={setLiveMarket1}
            liveMarket2={liveMarket2} setLiveMarket2={setLiveMarket2}
            liveMarket3={liveMarket3} setLiveMarket3={setLiveMarket3}
            liveMarket4={liveMarket4} setLiveMarket4={setLiveMarket4}
            liveMarket5={liveMarket5} setLiveMarket5={setLiveMarket5}
            upcomingMarket1={upcomingMarket1} setUpcomingMarket1={setUpcomingMarket1}
            upcomingMarket2={upcomingMarket2} setUpcomingMarket2={setUpcomingMarket2}
            upcomingMarket3={upcomingMarket3} setUpcomingMarket3={setUpcomingMarket3}
            upcomingMarket4={upcomingMarket4} setUpcomingMarket4={setUpcomingMarket4}
            upcomingMarket5={upcomingMarket5} setUpcomingMarket5={setUpcomingMarket5}
            openDropdown={openDropdown}
            setOpenDropdown={setOpenDropdown}
            preMarketOptions={getMarketOptionsForSport(activePreSport, false)}
            liveMarketOptions={getMarketOptionsForSport(activeLiveSport, true)}
            renderMarketColHeader={renderMarketColHeader} renderMarketColCells={renderMarketColCells}
            handleEventClick={handleEventClick} formatTime={formatTime} formatClock={(ev) => formatClock(ev, t)}
            onNavigate={onNavigate} onLoginClick={onLoginClick} allLiveSportTabs={allLiveSportTabs}
            sortBy={sortBy} setSortBy={setSortBy} liveSportEvents={liveSportEvents}
            tournamentGroups={tournamentGroups} sorted={sorted} next6h={next6h}
            columnHeaders={columnHeaders} upcomingColumnHeaders={upcomingColumnHeaders} renderLiveOddsRow={renderLiveOddsRow} renderNext6hRow={renderNext6hRow}
            hasSelectedEvent={!!selectedEvent}
            loadingMoreLive={loadingMoreLive}
            onLoadMoreLiveUpcoming={loadMoreLiveUpcoming}
            hasActiveFilters={!!quickFilter || selectedTournaments.size > 0}
          />
        </main>

        {selectedEvent && (
          <div className="w-[390px] flex-shrink-0 flex flex-col bg-white rounded-lg shadow-xl overflow-hidden border border-gray-200 sticky top-[80px] h-[calc(100vh-100px)] self-start">
            <div className="flex-1 overflow-y-auto min-h-0">
              <MatchDetailPage
                event={selectedEvent}
                onBack={() => setSelectedEvent(null)}
                bets={bets}
                onAddBet={onAddBet}
                onRemoveBet={onRemoveBet}
                onUpdateBet={onUpdateBet}
                onLoginClick={onLoginClick}
                initialMarketTab={detailInitialTab}
              />
            </div>
          </div>
        )}

        <DesktopRightSidebar
          bets={bets}
          tickets={tickets}
          rightSidebarTab={rightSidebarTab}
          setRightSidebarTab={setRightSidebarTab}
          onClearAll={onClearAll}
          onRemoveBet={onRemoveBet}
          onUpdateBet={onUpdateBet}
          totalOdds={displayTotalOdds}
          stake={stake}
          setStake={setStake}
          possibleWin={possibleWin}
          betStatus={betStatus}
          betError={betError}
          isLogged={isLogged}
          onLoginClick={onLoginClick}
          handlePlaceBet={handlePlaceBet}
          placing={placing}
          stakeNum={stakeNum}
          betslipMode={betslipMode}
          setBetslipMode={setBetslipMode}
          individualStakes={individualStakes}
          setIndividualStakes={setIndividualStakes}
          systemSize={systemSize}
          setSystemSize={setSystemSize}
        />
      </div>

      {activePage !== 'live' && (
        <Footer locale={locale} setLocale={setLocale} />
      )}

      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        allEvents={allEventsForSearch}
        onNavigate={onNavigate}
        onEventClick={handleEventClick}
        onAddBet={handleOddsClick}
        isSelected={isSelected}
      />

      {placing && createPortal(
        <div className="fixed inset-0 bg-white/90 z-[999999] flex flex-col items-center justify-center backdrop-blur-sm">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-tipico-red border-t-transparent mb-5 shadow-sm" />
          <p className="text-[20px] font-black text-gray-800 tracking-wide uppercase">{t('slip.placingBet')}</p>
          <p className="text-[15px] text-gray-500 mt-1 font-medium">{t('slip.placingWait')}</p>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DesktopHomePage;
